import {
  Activity,
  ArrowRight,
  BarChart3,
  BadgeCheck,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Dumbbell,
  Footprints,
  Home,
  Instagram,
  MapPin,
  Menu,
  MessageCircle,
  ShieldCheck,
  UserCheck,
  Waves,
  X
} from "lucide-react";
import type { CSSProperties, FormEvent, ReactNode } from "react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import reviewsData from "./data/reviews.json";

type Locale = "en" | "es";
type PageKey =
  | "home"
  | "functional"
  | "method"
  | "plans"
  | "competition"
  | "app"
  | "jorge"
  | "check"
  | "contact"
  | "privacy"
  | "terms"
  | "cookies"
  | "medicalDisclaimer";
type ContentPageKey = Exclude<PageKey, "home" | "functional" | "check" | "contact" | "jorge" | "plans" | "method" | "competition">;
type LegalPageKey = "privacy" | "terms" | "cookies" | "medicalDisclaimer";

type RouteState = {
  locale: Locale;
  page: PageKey;
};

type VideoAsset = {
  poster: string;
  src?: string;
};

type PhoneLoopAsset = {
  poster: string;
  mp4: string;
  webm: string;
};

type ReviewEntry = {
  locale: Locale;
  quote: string;
  author: string;
  rating: number;
  sourceName: string;
  sourceUrl: string;
};

const SITE_ORIGIN = "https://personaltrainercostadelsol.com";
const MEDIA_VERSION = "20260705-battle-ropes-hero-v2";
const WHATSAPP_TARGET_NUMBER = "34604430510";
const INSTAGRAM_URL = "https://www.instagram.com/jorgetardioborrego/";
const CONSENT_KEY = "ptcs-consent-v2";
const SOCIAL_IMAGE = "/assets/hero-videos/ptcs-home-hero-costa-battle-ropes-moving-lovable-20260705-v2.jpg";
// GA4 Measurement ID. Leave empty to keep analytics dormant (no tags load, no
// errors). Set to the real "G-XXXXXXXXXX" to activate Google Analytics 4.
// Consent Mode v2 is already wired: nothing is stored until the visitor accepts.
const GA_MEASUREMENT_ID: string = "G-E4XEBY7VHS";

const paths: Record<PageKey, Record<Locale, string>> = {
  home: { en: "/en", es: "/es" },
  functional: { en: "/en/functional-training-costa-del-sol", es: "/es/entrenamiento-funcional-costa-del-sol" },
  method: { en: "/en/method", es: "/es/metodo" },
  plans: { en: "/en/plans", es: "/es/planes" },
  competition: { en: "/en/competition-prep", es: "/es/preparacion-competicion" },
  app: { en: "/en/app", es: "/es/app" },
  jorge: { en: "/en/jorge", es: "/es/jorge" },
  check: { en: "/en/first-step", es: "/es/primer-paso" },
  contact: { en: "/en/contact", es: "/es/contacto" },
  privacy: { en: "/en/privacy", es: "/es/privacidad" },
  terms: { en: "/en/terms", es: "/es/condiciones" },
  cookies: { en: "/en/cookies", es: "/es/cookies" },
  medicalDisclaimer: { en: "/en/medical-disclaimer", es: "/es/aviso-medico" }
};

const pathIndex = Object.entries(paths).reduce((acc, [page, variants]) => {
  (Object.keys(variants) as Locale[]).forEach((locale) => {
    acc[variants[locale]] = { page: page as PageKey, locale };
  });
  return acc;
}, {} as Record<string, RouteState>);

const legacyPaths: Record<string, RouteState> = {
  "/": { page: "home", locale: "en" },
  "/functional-training-costa-del-sol": { page: "functional", locale: "en" },
  "/entrenamiento-funcional-costa-del-sol": { page: "functional", locale: "es" },
  "/method": { page: "method", locale: "en" },
  "/plans": { page: "plans", locale: "en" },
  "/competition": { page: "competition", locale: "en" },
  "/en/competition": { page: "competition", locale: "en" },
  "/es/competicion": { page: "competition", locale: "es" },
  "/app": { page: "app", locale: "en" },
  "/jorge": { page: "jorge", locale: "en" },
  "/strength-balance-check": { page: "check", locale: "en" },
  "/en/strength-balance-check": { page: "check", locale: "en" },
  "/es/test-fuerza-equilibrio": { page: "check", locale: "es" },
  "/try-one-day": { page: "check", locale: "en" },
  "/en/try-one-day": { page: "check", locale: "en" },
  "/probar-un-dia": { page: "check", locale: "es" },
  "/es/probar-un-dia": { page: "check", locale: "es" },
  "/contact": { page: "contact", locale: "en" },
  "/privacy": { page: "privacy", locale: "en" },
  "/terms": { page: "terms", locale: "en" },
  "/cookies": { page: "cookies", locale: "en" },
  "/medical-disclaimer": { page: "medicalDisclaimer", locale: "en" }
};

const legacyRedirects: Record<string, string> = {
  "/strength-balance-check": paths.check.en,
  "/en/strength-balance-check": paths.check.en,
  "/es/test-fuerza-equilibrio": paths.check.es,
  "/try-one-day": paths.check.en,
  "/en/try-one-day": paths.check.en,
  "/probar-un-dia": paths.check.es,
  "/es/probar-un-dia": paths.check.es
};

function withVersion(path: string) {
  return `${path}?v=${MEDIA_VERSION}`;
}

function trackEvent(event: string, detail: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  const payload = { event, ...detail, at: new Date().toISOString() };
  const dataLayerWindow = window as Window & {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  };
  if (hasAnalyticsConsent()) {
    dataLayerWindow.dataLayer?.push(payload);
    try {
      localStorage.setItem("ptcs-last-event", JSON.stringify(payload));
    } catch {
      // Analytics storage must never interrupt the user flow.
    }
  }
  // Forward to GA4. Consent Mode v2 decides whether it is stored or modelled.
  if (GA_MEASUREMENT_ID && typeof dataLayerWindow.gtag === "function") {
    dataLayerWindow.gtag("event", event, detail);
  }
  window.dispatchEvent(new CustomEvent("ptcs:event", { detail: payload }));
}

function hasAnalyticsConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY) === "accepted";
  } catch {
    return false;
  }
}

function buildWhatsAppUrl(message: string) {
  const body = encodeURIComponent(message);
  return WHATSAPP_TARGET_NUMBER ? `https://wa.me/${WHATSAPP_TARGET_NUMBER}?text=${body}` : `https://api.whatsapp.com/send?text=${body}`;
}

const videos = {
  heroStory: {
    poster: "/assets/lovable-generated/ptcs-hero-costa-jorge-coach-walk-v4.png"
  },
  heroTraining: {
    poster: "/assets/lovable-generated/ptcs-hero-gym-black-coach-heavy-strength-v4.png"
  },
  hybridWoman: {
    poster: "/assets/lovable-generated/ptcs-hybrid-young-woman-barbell-jump-squat-v4.png"
  },
  seniorMan: {
    poster: "/assets/lovable-generated/ptcs-senior-man-heavy-strength-close-v4.png"
  },
  coachFloor: {
    poster: "/assets/lovable-generated/ptcs-method-jorge-floor-coaching-v4.png"
  },
  premiumStory: {
    poster: "/assets/lovable-generated/ptcs-method-device-strength-session-v4.png"
  },
  methodCoach: {
    poster: "/assets/lovable-generated/ptcs-method-adaptive-plan-heavy-gym-v4.png"
  },
  online: {
    poster: "/assets/lovable-generated/ptcs-online-close-chest-press-v4.png"
  },
  annual: {
    poster: "/assets/lovable-generated/ptcs-annual-senior-kettlebell-strength-v4.png"
  },
  contactRunner: {
    poster: "/assets/lovable-generated/ptcs-contact-senior-man-costa-strength-v5.png"
  }
} satisfies Record<string, VideoAsset>;

const phoneLoops = {
  seniorStrength: {
    poster: "/assets/phone-videos/ptcs-phone-senior-strength-loop-lovable-20260703.jpg",
    mp4: "/assets/phone-videos/ptcs-phone-senior-strength-loop-lovable-20260703.mp4",
    webm: "/assets/phone-videos/ptcs-phone-senior-strength-loop-lovable-20260703.webm"
  },
  adultMobility: {
    poster: "/assets/phone-videos/ptcs-phone-adult-mobility-loop-lovable-20260703.jpg",
    mp4: "/assets/phone-videos/ptcs-phone-adult-mobility-loop-lovable-20260703.mp4",
    webm: "/assets/phone-videos/ptcs-phone-adult-mobility-loop-lovable-20260703.webm"
  }
} satisfies Record<string, PhoneLoopAsset>;

const heroLoops = {
  homeCosta: {
    poster: "/assets/hero-videos/ptcs-home-hero-costa-battle-ropes-moving-lovable-20260705-v2.jpg",
    mp4: "/assets/hero-videos/ptcs-home-hero-costa-battle-ropes-moving-lovable-20260705-v2.mp4",
    webm: "/assets/hero-videos/ptcs-home-hero-costa-battle-ropes-moving-lovable-20260705-v2.webm"
  }
} satisfies Record<string, PhoneLoopAsset>;

const realReviews = reviewsData as ReviewEntry[];

const images = {
  heroReferenceBattle: "/assets/client/ptcs-hero-costa-battle-ropes-reference-optimized.jpg",
  jorge: "/assets/client/jorge-tardio-real-stage-portrait-20260629.jpg",
  routesCoast: "/assets/lovable-generated/ptcs-routes-costa-consistent-v4.jpg",
  advancedStrengthWoman: "/assets/lovable-generated/ptcs-advanced-lovable-woman-defined-strength-20260706.jpg",
  advancedStrengthWomanMedium: "/assets/lovable-generated/ptcs-advanced-lovable-woman-defined-strength-20260706-1000.jpg",
  advancedStrengthWomanSmall: "/assets/lovable-generated/ptcs-advanced-lovable-woman-defined-strength-20260706-600.jpg",
  advancedStrengthMan: "/assets/lovable-generated/ptcs-advanced-lovable-man-bodybuilder-strength-20260706.jpg",
  advancedStrengthManMedium: "/assets/lovable-generated/ptcs-advanced-lovable-man-bodybuilder-strength-20260706-1000.jpg",
  advancedStrengthManSmall: "/assets/lovable-generated/ptcs-advanced-lovable-man-bodybuilder-strength-20260706-600.jpg",
  advancedStrengthDetail: "/assets/lovable-generated/ptcs-advanced-lovable-man-bodybuilder-strength-detail-20260706.jpg",
  refHeroSenior: "/assets/reference-realistic/ref-hero-senior-clean.jpg",
  refLifestyleHiker: "/assets/reference-realistic/ref-lifestyle-hiker.jpg",
  refLifestyleGolf: "/assets/reference-realistic/ref-lifestyle-golf.jpg",
  refLifestyleLunge: "/assets/reference-realistic/ref-lifestyle-lunge.jpg",
  refLifestyleBeachWalk: "/assets/reference-realistic/ref-lifestyle-beach-walk.jpg",
  refLifestyleSled: "/assets/reference-realistic/ref-lifestyle-sled.jpg",
  refRouteOnlineTight: "/assets/reference-realistic/ref-route-online-person-tight.jpg",
  refRouteHomeTight: "/assets/reference-realistic/ref-route-home-coaching-tight.jpg",
  refRouteAnnualTight: "/assets/reference-realistic/ref-route-annual-phone-tight.jpg",
  workoutGolf: "/assets/editorial/ptcs-workout-golf-costa-del-sol-v1.jpg",
  workoutSeaBalance: "/assets/editorial/ptcs-workout-sea-balance-v1.jpg",
  workoutDiverseTraining: "/assets/editorial/ptcs-workout-diverse-training-v1.jpg",
  workoutPrivateTerrace: "/assets/editorial/ptcs-workout-private-terrace-v1.jpg"
};

const jorgeGallery = [
  {
    src: "/assets/client/jorge-gallery/jorge-gallery-gym-mirror-2023-a.jpg",
    caption: { en: "Mijas · training room", es: "Mijas · trabajo de sala" },
    alt: {
      en: "Training room pose during a definition phase in Mijas",
      es: "Pose en sala durante una etapa de definición en Mijas"
    }
  },
  {
    src: "/assets/client/jorge-gallery/jorge-gallery-stage-close-archive.jpg",
    caption: { en: "Competition archive", es: "Archivo competitivo" },
    alt: {
      en: "Competition archive in a bodybuilding pose",
      es: "Archivo competitivo en pose de culturismo"
    }
  },
  {
    src: "/assets/client/jorge-gallery/jorge-gallery-stage-apartment-2015.jpg",
    caption: { en: "Before competition · 2015", es: "Antes de competir · 2015" },
    alt: {
      en: "Preparation photo before competing in 2015",
      es: "Fotografía de preparación antes de competir en 2015"
    }
  },
  {
    src: "/assets/client/jorge-gallery/jorge-gallery-beach-archive-2017.jpg",
    caption: { en: "Coast · October 2017", es: "Costa · octubre 2017" },
    alt: {
      en: "Beach photo during a competition phase",
      es: "Fotografía en la playa durante etapa competitiva"
    }
  }
] as const;

const copy = {
  en: {
    brand: "Personal Trainer Costa del Sol",
    byline: "Jorge Tardío",
    nav: {
      method: "Method",
      plans: "Plans",
      competition: "Advanced",
      jorge: "Jorge",
      contact: "WhatsApp"
    },
    meta: {
      title: "Personal Trainer on the Costa del Sol | Jorge Tardío",
      description:
        "Personal training on the Costa del Sol with Jorge Tardío. Build strength, move with confidence and enjoy life more, at your pace and with no pressure."
    },
    cta: {
      primary: "Improve your quality of life",
      consultation: "Talk to Jorge and his team",
      secondary: "View plans",
      whatsapp: "Prepare WhatsApp",
      contact: "Contact Jorge and his team",
      method: "See the method",
      jorge: "Read Jorge's story",
      competition: "Advanced coaching"
    },
    hero: {
      eyebrow: "Personal training • Costa del Sol",
      title: "Train to live better.",
      body:
        "More strength for your everyday life. And your health thanks you for it: more energy, better sleep, a body that responds.",
      proof: "Train with Jorge's method",
      chips: ["Start where you are", "No pressure", "At your pace"]
    },
    pillars: [
      ["Build strength", "For everyday life"],
      ["Gain balance", "Feel steady on your feet"],
      ["Live your way", "Without depending on anyone"]
    ],
    intro: {
      title: "Move better in your daily life.",
      body:
        "Stairs without thinking twice. Getting up and down without drama. Carrying the shopping. Small things that, when they come back, change the mood of your week."
    },
    method: {
      title: "The Jorge Tardío Method starts before the first workout.",
      body:
        "First we talk. Then we see how you are. Only then do we build your plan. No rush, no jargon and nothing to pay until it feels right.",
      steps: [
        ["A first chat", "Tell us how you are, what you want and what worries you. Just a conversation."],
        ["We see your starting point", "We look at how you move and what you have at hand. Calmly, with no tests to pass."],
        ["Your weekly plan", "Strength, balance and good habits. You know what each day is for."],
        ["The plan adapts to you", "Some weeks are rough. The plan knows: it adapts, it does not punish you."],
        ["You are never alone", "Jorge and his team answer you on WhatsApp. A person replies. With a name. Not a bot."]
      ],
      stats: [
        ["40+", "Years of experience"],
        ["2002-2024", "National trophies"],
        ["1 on 1", "You train one-to-one, with Jorge's method"]
      ]
    },
    plans: {
      title: "There is a way of training that fits your life.",
      body:
        "You do not have to figure it out alone. Tell us how you are, how much time you have and what you want. Jorge tells you where to start. Nothing to pay, no commitment.",
      cards: [
        ["Home Training", "From 90 EUR / session", "Jorge comes to your home. No mirrors, no stares, no posing. Strength and balance with someone who never judges.", "Costa del Sol"],
        ["Annual Premium", "Ask our team", "A whole year with support. Consistency does your body good, and your head too.", "For the whole year"],
        ["Online Coaching", "100 EUR / month", "Your weekly plan wherever you are. The team checks your progress and helps you keep going.", "From anywhere"],
        ["Technique Review", "60 EUR / session", "Your online plan plus in-person sessions to fix posture and move better.", "Plan plus technique"]
      ]
    },
    comparison: {
      title: "Why is an app not enough?",
      body:
        "An app counts sets, but it does not know how hard you push. To move forward you have to leave your comfort zone, and that takes someone watching. Jorge sees when to ask for a little more and when to ease off, always within your level.",
      rows: [
        ["A phone app", "The same routine for everyone. It cannot tell if you push or when to stop."],
        ["Jorge's method", "He knows you and knows when to ask for a little more, without going past your level."],
        ["The result", "You truly progress. With the right effort and someone waiting for you, you do not quit."]
      ]
    },
    competition: {
      title: "Already training and want more? We help you get there.",
      body:
        "If you want a bigger challenge, Jorge's team guides you step by step. No hype: years on stage and in real gyms.",
      disciplines: ["Strength", "Movement", "Bodybuilding", "Consistency", "Guidance"],
      note: "First we talk. No pressure."
    },
    jorge: {
      subtitle: "Personal trainer on the Costa del Sol. More than 40 years helping people get stronger, feel safer and do more by themselves.",
      title: "I help you live better with your body.",
      intro:
        "You do not need to be fit or have ever set foot in a gym. We start from where you are and go at your pace.",
      body:
        "First I listen to you. You may want more strength, more confidence, sport again or more trust in your body.",
      body2:
        "From 2002 to 2024 I won several national bodybuilding championships and took part in the Arnold Classic. I do not say this to impress you. That experience helps me see more, correct better and take care of you.",
      body3:
        "I have spent decades training all kinds of people. The first day feels intimidating, it happens to almost everyone. And I know something else: people come to look better and stay because they feel better. They sleep better, last longer, wake up in a better mood. That, to me, is health.",
      role:
        "My role is simple: help you gain strength, move with confidence and do more every day."
    },
    contact: {
      title: "Your first step is a simple WhatsApp message.",
      body:
        "Tell us how you are and what you would like to achieve. We reply fast and tell you where to start. Nothing to pay, no commitment.",
      prompt:
        "Hi Jorge, I want to start training but I do not know where to begin. Can you point me in the right direction?"
    },
    legal: {
      privacy: [
        "Privacy",
        "We only ask for details needed to answer you, prepare your WhatsApp conversation or support your training. Health details and progress photos stay private unless you give permission."
      ],
      terms: [
        "Terms",
        "Plans are confirmed before you decide. Annual packages last twelve months. Unused sessions are not saved or moved to the next period."
      ],
      cookies: ["Cookies", "Essential cookies keep the site working. Analytics and marketing cookies are not enabled unless you consent."],
      medicalDisclaimer: [
        "Medical disclaimer",
        "This service helps with strength, balance and healthy habits. It does not diagnose, prescribe, treat injuries, replace medical care or give rehabilitation advice."
      ]
    },
    form: {
      name: "Name",
      message: "How can we help you?",
      success: "Your WhatsApp message is ready to send."
    },
    footer: {
      promise: "Training is taking care of yourself. Start when you want.",
      credit: "Website by Kemdrix Studio"
    }
  },
  es: {
    brand: "Personal Trainer Costa del Sol",
    byline: "Jorge Tardío",
    nav: {
      method: "Método",
      plans: "Planes",
      competition: "Avanzado",
      jorge: "Jorge",
      contact: "WhatsApp"
    },
    meta: {
      title: "Entrenador personal en la Costa del Sol | Jorge Tardío",
      description:
        "Entrenamiento personal en la Costa del Sol con Jorge Tardío. Gana fuerza, muévete con seguridad y disfruta más de tu vida, a tu ritmo y sin presión."
    },
    cta: {
      primary: "Mejora tu calidad de vida",
      consultation: "Habla con Jorge y su equipo",
      secondary: "Ver planes",
      whatsapp: "Preparar WhatsApp",
      contact: "Contacta con Jorge y su equipo",
      method: "Ver el método",
      jorge: "Leer la historia de Jorge",
      competition: "Entrenamiento avanzado"
    },
    hero: {
      eyebrow: "Entrenamiento personal • Costa del Sol",
      title: "Entrena para vivir mejor.",
      body:
        "Más fuerza para tu día a día. Y de paso, salud: más energía, mejor descanso y un cuerpo que responde.",
      proof: "Entrena con el método de Jorge",
      chips: ["Empieza donde estás", "Sin presión", "A tu ritmo"]
    },
    pillars: [
      ["Gana fuerza", "Para tu día a día"],
      ["Gana equilibrio", "Pisa firme y seguro"],
      ["Vive a tu manera", "Sin depender de nadie"]
    ],
    intro: {
      title: "Muévete mejor en tu día a día.",
      body:
        "Subir escaleras sin pensarlo. Agacharte y levantarte sin drama. Cargar la compra. Cosas pequeñas que, cuando vuelven, cambian el humor de la semana."
    },
    method: {
      title: "El Método Jorge Tardío empieza antes del primer entrenamiento.",
      body:
        "Primero hablamos. Después vemos cómo estás. Y solo entonces montamos tu plan. Sin prisa, sin palabras raras y sin pagar nada hasta que lo veas claro.",
      steps: [
        ["Primera charla", "Nos cuentas cómo estás, qué quieres y qué te preocupa. Solo hablar."],
        ["Vemos tu punto de partida", "Miramos cómo te mueves y qué tienes a mano. Con calma y sin exámenes."],
        ["Tu plan de la semana", "Fuerza, equilibrio y buenos hábitos. Sabes qué toca cada día y para qué sirve."],
        ["El plan se adapta a ti", "Hay semanas malas. El plan lo sabe: se adapta, no te castiga."],
        ["Nunca estás solo", "Jorge y su equipo te responden por WhatsApp. Te contesta una persona. Con nombre. No un bot."]
      ],
      stats: [
        ["40+", "Años ayudando a entrenar"],
        ["2002-2024", "Trofeos nacionales"],
        ["1 a 1", "Entrenas tú solo, con el método de Jorge"]
      ]
    },
    plans: {
      title: "Hay una forma de entrenar que encaja con tu vida.",
      body:
        "No tienes que acertar tú solo. Cuéntanos cómo estás, cuánto tiempo tienes y qué quieres conseguir. Jorge te dice por dónde empezar. Sin pagar nada y sin compromiso.",
      cards: [
        ["Entrenamiento en casa", "Desde 90 EUR / sesión", "Jorge va a tu casa. Sin espejos, sin miradas, sin postureo. Fuerza y equilibrio con alguien que no te juzga.", "Costa del Sol"],
        ["Premium Anual", "Consulta con nuestro equipo", "Un año entero acompañado. Al cuerpo le sienta bien la constancia, y a la cabeza también.", "Para todo el año"],
        ["Entrenamiento Online", "100 EUR / mes", "Tu plan semanal estés donde estés. El equipo revisa cómo vas y te ayuda a no dejarlo.", "Desde cualquier lugar"],
        ["Revisión de Técnica", "60 EUR / sesión", "Tu plan online y sesiones en persona para corregir la postura y moverte mejor.", "Plan más técnica"]
      ]
    },
    comparison: {
      title: "¿Por qué no me basta con una app?",
      body:
        "Una app cuenta series, pero no sabe cuánto te esfuerzas. Para avanzar hay que salir de la zona cómoda, y eso pide a alguien que te mire. Jorge ve cuándo pedirte un poco más y cuándo frenar, siempre dentro de tu nivel.",
      rows: [
        ["Una app del móvil", "La misma rutina para todos. No sabe si aprietas ni cuándo parar."],
        ["El método de Jorge", "Te conoce y sabe cuándo pedirte un poco más, sin pasarte de tu nivel."],
        ["El resultado", "Progresas de verdad. Con el esfuerzo justo y alguien que te espera, no lo dejas."]
      ]
    },
    competition: {
      title: "¿Ya entrenas y quieres más? Te ayudamos a conseguirlo.",
      body:
        "Si buscas un reto más grande, el equipo de Jorge te guía paso a paso. Sin humo: años de tarima y de gimnasio de verdad.",
      disciplines: ["Fuerza", "Movimiento", "Culturismo", "Constancia", "Seguimiento"],
      note: "Primero hablamos. Sin presión."
    },
    jorge: {
      subtitle: "Entrenador personal en la Costa del Sol. Más de 40 años ayudando a ganar fuerza, seguridad y capacidad para hacer más cosas.",
      title: "Te ayudo a vivir mejor con tu cuerpo.",
      intro:
        "No hace falta estar en forma ni haber pisado un gimnasio. Empezamos desde donde estás y vamos a tu ritmo.",
      body:
        "Primero te escucho. Quizá quieres más fuerza, más confianza, volver al deporte o fiarte más de tu cuerpo.",
      body2:
        "De 2002 a 2024 gané varios campeonatos nacionales de culturismo y participé en el Arnold Classic. No lo digo para impresionarte. Esa experiencia me ayuda a ver más, corregir mejor y cuidar de ti.",
      body3:
        "Llevo décadas entrenando a personas de todo tipo. Sé que el primer día impone, a casi todos les pasa. Y sé otra cosa: la gente viene por verse mejor y se queda porque se encuentra mejor. Duerme mejor, aguanta más, está de mejor humor. Eso, para mí, es salud.",
      role:
        "Mi papel es sencillo: que ganes fuerza, te muevas con seguridad y hagas más cada día."
    },
    contact: {
      title: "Tu primer paso es un simple WhatsApp.",
      body:
        "Cuéntanos cómo estás y qué te gustaría conseguir. Te respondemos rápido y te decimos por dónde empezar. Sin pagar nada y sin compromiso.",
      prompt:
        "Hola Jorge, quiero empezar a entrenar pero no sé por dónde. ¿Me orientas?"
    },
    legal: {
      privacy: [
        "Privacidad",
        "Solo pedimos los datos necesarios para responder, preparar tu conversación por WhatsApp o apoyar tu entrenamiento. Los datos de salud y las fotos de progreso son privados salvo permiso."
      ],
      terms: [
        "Condiciones",
        "Los planes se confirman antes de decidir. Los paquetes anuales duran doce meses. Las sesiones no usadas no se guardan ni pasan al siguiente periodo."
      ],
      cookies: ["Cookies", "Las cookies esenciales mantienen la web funcionando. Analítica y marketing no se activan sin consentimiento."],
      medicalDisclaimer: [
        "Aviso médico",
        "Este servicio ayuda con fuerza, equilibrio y hábitos saludables. No diagnostica, prescribe, trata lesiones, sustituye atención médica ni da rehabilitación."
      ]
    },
    form: {
      name: "Nombre",
      message: "¿En qué te podemos ayudar?",
      success: "Tu mensaje de WhatsApp está listo para enviar."
    },
    footer: {
      promise: "Entrenar es cuidarse. Empieza cuando quieras.",
      credit: "Web creada por Kemdrix Studio"
    }
  }
} as const;

const navKeys: PageKey[] = ["method", "plans", "competition", "jorge", "contact"];
const legalKeys: LegalPageKey[] = ["privacy", "terms", "cookies", "medicalDisclaimer"];

function getRouteSeo(locale: Locale, page: PageKey) {
  const t = copy[locale];
  const isEn = locale === "en";

  switch (page) {
    case "home":
      return t.meta;
    case "functional":
      return isEn
        ? {
            title: "Functional Training on the Costa del Sol | Jorge Tardío",
            description:
              "Functional personal training for strength, mobility and balance on the Costa del Sol. Train for stairs, travel, golf and everyday life at your level."
          }
        : {
            title: "Entrenamiento funcional en la Costa del Sol | Jorge Tardío",
            description:
              "Entrenamiento funcional personalizado para ganar fuerza, movilidad y equilibrio en la Costa del Sol. Entrena para escaleras, viajes, golf y tu día a día."
          };
    case "method":
      return isEn
        ? {
            title: "The Jorge Tardío Method | Personal Trainer Costa del Sol",
            description:
              "A personal training method built around your life, availability, equipment and level, with clear weekly plans, continuous adaptation and WhatsApp support."
          }
        : {
            title: "Método Jorge Tardío | Entrenador personal Costa del Sol",
            description:
              "Un método de entrenamiento personal adaptado a tu vida, disponibilidad, material y nivel, con plan semanal, ajustes continuos y seguimiento por WhatsApp."
          };
    case "plans":
      return isEn
        ? {
            title: "Personal Training Plans | Costa del Sol",
            description:
              "At-home personal training, annual support, online coaching and technique review. Choose the service that fits your life on the Costa del Sol."
          }
        : {
            title: "Planes de entrenamiento personal | Costa del Sol",
            description:
              "Entrenamiento a domicilio, seguimiento anual, entrenamiento online y revisión de técnica. Elige la modalidad que encaja con tu vida."
          };
    case "competition":
      return isEn
        ? {
            title: "Bodybuilding & Bikini Competition Prep | Costa del Sol",
            description:
              "Bodybuilding, bikini fitness and athletic performance preparation with Jorge Tardío on the Costa del Sol, from planning to competition."
          }
        : {
            title: "Preparación de culturismo y bikini fitness | Costa del Sol",
            description:
              "Preparación de culturismo, bikini fitness y rendimiento atlético con Jorge Tardío en la Costa del Sol, desde la planificación hasta la competición."
          };
    case "jorge":
      return isEn
        ? {
            title: "Jorge Tardío Borrego, Personal Trainer | Costa del Sol",
            description:
              "Meet Jorge Tardío Borrego: more than 40 years of experience, national trophies from 2002 to 2024 and Arnold Classic participation."
          }
        : {
            title: "Jorge Tardío Borrego, entrenador personal | Costa del Sol",
            description:
              "Conoce a Jorge Tardío Borrego: más de 40 años de experiencia, trofeos nacionales entre 2002 y 2024 y participación en Arnold Classic."
          };
    case "check":
      return isEn
        ? {
            title: "Your First Step | Personal Trainer Costa del Sol",
            description:
              "Tell us your location, level, preferred training format and goal. We prepare a simple first conversation with Jorge's team on WhatsApp."
          }
        : {
            title: "Tu primer paso | Entrenador personal Costa del Sol",
            description:
              "Indica tu ubicación, nivel, modalidad preferida y objetivo. Preparamos una primera conversación sencilla por WhatsApp con el equipo de Jorge."
          };
    case "contact":
      return isEn
        ? {
            title: "Contact Jorge Tardío | Personal Trainer Costa del Sol",
            description:
              "Contact Jorge Tardío for personal training on the Costa del Sol. Start with a simple WhatsApp conversation, with no pressure or commitment."
          }
        : {
            title: "Contacto Jorge Tardío | Entrenador Costa del Sol",
            description:
              "Contacta con Jorge Tardío para entrenamiento personal en la Costa del Sol. Empieza con una conversación sencilla por WhatsApp y sin compromiso."
          };
    case "app":
      return {
        title: `${isEn ? "Private Client Area" : "Área privada del cliente"} | ${t.brand}`,
        description: isEn
          ? "Client portal preview for approved Personal Trainer Costa del Sol clients."
          : "Vista preliminar del portal cliente para clientes aprobados de Personal Trainer Costa del Sol."
      };
    default:
      return {
        title: `${t.legal[page as LegalPageKey][0]} | ${t.brand}`,
        description: t.legal[page as LegalPageKey][1]
      };
  }
}

function normalizePath(pathname: string) {
  const clean = pathname.replace(/\/+$/, "");
  return clean || "/";
}

function resolveRoute(pathname: string): RouteState {
  const path = normalizePath(pathname);
  if (pathIndex[path]) return pathIndex[path];
  if (legacyPaths[path]) return legacyPaths[path];
  return { page: "home", locale: path.startsWith("/es") ? "es" : "en" };
}

function useRoute() {
  const [route, setRoute] = useState<RouteState>(() => resolveRoute(window.location.pathname));

  const navigate = useCallback((href: string) => {
    window.history.pushState({}, "", href);
    setRoute(resolveRoute(window.location.pathname));
  }, []);

  useEffect(() => {
    const canonicalPath = legacyRedirects[normalizePath(window.location.pathname)];
    if (!canonicalPath) return;
    window.history.replaceState({}, "", `${canonicalPath}${window.location.search}${window.location.hash}`);
    setRoute(resolveRoute(canonicalPath));
  }, []);

  useEffect(() => {
    const onPop = () => setRoute(resolveRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  return { route, navigate };
}

function useRouteScrollReset(route: RouteState) {
  useEffect(() => {
    if (window.location.hash) return;
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [route.locale, route.page]);
}

function setMeta(selector: string, value: string, attribute = "content") {
  const node = document.querySelector<HTMLMetaElement | HTMLLinkElement>(selector);
  if (node) node.setAttribute(attribute, value);
}

function altFormat(src: string, ext: string) {
  return src.replace(/\.(png|jpe?g)$/i, ext);
}

// Serves AVIF (then WebP) to browsers that support them, falling back to the
// original raster for everyone else, so quality is never worse than before.
function SmartImg({
  src,
  alt,
  className,
  label,
  loading
}: {
  src: string;
  alt: string;
  className: string;
  label?: string;
  loading: "lazy" | "eager";
}) {
  const img = (
    <img
      className={className}
      src={withVersion(src)}
      alt={alt}
      aria-label={label}
      loading={loading}
      decoding="async"
    />
  );
  if (!/\.(png|jpe?g)$/i.test(src)) return img;
  return (
    <picture>
      <source type="image/avif" srcSet={withVersion(altFormat(src, ".avif"))} />
      <source type="image/webp" srcSet={withVersion(altFormat(src, ".webp"))} />
      {img}
    </picture>
  );
}

function MotionVideo({
  visual,
  className = "",
  label,
  eager = false
}: {
  visual: VideoAsset;
  className?: string;
  label?: string;
  eager?: boolean;
}) {
  if (visual.src) {
    return (
      <video
        className={`motion-video ${className}`.trim()}
        src={withVersion(visual.src)}
        poster={withVersion(visual.poster)}
        aria-label={label}
        muted
        loop
        playsInline
        autoPlay
        preload={eager ? "auto" : "metadata"}
      />
    );
  }
  return (
    <SmartImg
      src={visual.poster}
      alt={label ?? ""}
      label={label}
      className={`motion-video ${className}`.trim()}
      loading={eager ? "eager" : "lazy"}
    />
  );
}

function StillImage({
  src,
  alt,
  className = "",
  loading = "lazy"
}: {
  src: string;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
}) {
  return <SmartImg src={src} alt={alt} className={`pt-still-image ${className}`.trim()} loading={loading} />;
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  return reduced;
}

function formatTimer(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (totalSeconds % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function parseCountValue(value: string) {
  const trimmed = value.trim();
  const range = trimmed.match(/^(\d{2,4})\s*-\s*(\d{2,4})(.*)$/);
  if (range) {
    return {
      type: "range" as const,
      startA: Number(range[1]),
      startB: Number(range[2]),
      suffix: range[3] ?? ""
    };
  }

  const single = trimmed.match(/^(\d+)(\+?)$/);
  if (single) {
    return {
      type: "single" as const,
      end: Number(single[1]),
      plus: single[2] ?? "",
      suffix: ""
    };
  }

  return null;
}

function formatCountValue(value: string, progress: number) {
  const parsed = parseCountValue(value);
  if (!parsed) return value;

  const eased = 1 - Math.pow(1 - progress, 3);
  if (parsed.type === "range") {
    return `${Math.round(parsed.startA * eased)}-${Math.round(parsed.startB * eased)}${parsed.suffix}`;
  }
  return `${Math.round(parsed.end * eased)}${parsed.plus}${parsed.suffix}`;
}

function CountUpText({ value }: { value: string }) {
  const reduced = usePrefersReducedMotion();
  const ref = useRef<HTMLSpanElement | null>(null);
  const [displayValue, setDisplayValue] = useState(value);
  const countableValue = Boolean(parseCountValue(value));

  useEffect(() => {
    setDisplayValue(value);
    if (reduced || !countableValue) return;

    const node = ref.current;
    let frame = 0;
    let finishTimer = 0;
    let hasRun = false;

    const start = () => {
      if (hasRun) return;
      hasRun = true;
      const startedAt = performance.now();
      const finish = () => {
        if (frame) window.cancelAnimationFrame(frame);
        setDisplayValue(value);
      };
      finishTimer = window.setTimeout(finish, 980);
      const tick = (time: number) => {
        const progress = Math.min(1, (time - startedAt) / 900);
        if (progress >= 1) {
          if (finishTimer) window.clearTimeout(finishTimer);
          finish();
          return;
        }
        setDisplayValue(formatCountValue(value, progress));
        frame = window.requestAnimationFrame(tick);
      };
      frame = window.requestAnimationFrame(tick);
    };

    if (!node || typeof window === "undefined" || !("IntersectionObserver" in window)) {
      start();
      return () => {
        if (finishTimer) window.clearTimeout(finishTimer);
        if (frame) window.cancelAnimationFrame(frame);
      };
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        start();
        observer.disconnect();
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.2 }
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
      if (finishTimer) window.clearTimeout(finishTimer);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [countableValue, reduced, value]);

  return (
    <span ref={ref} className={countableValue ? "pt-count-up" : "pt-count-static"} aria-label={value}>
      {displayValue}
    </span>
  );
}

function CountUpStepNumber({ value }: { value: string }) {
  return (
    <span className="pt-count-up pt-step-number-final" aria-label={value}>
      {value}
    </span>
  );
}

function CountUpPrice({ value }: { value: string }) {
  return (
    <span className="pt-price-static" aria-label={value}>
      {value}
    </span>
  );
}

function WordReveal({ text }: { text: string }) {
  const parts = text.split(/(\s+)/);
  let wordIndex = 0;
  return (
    <>
      {parts.map((part, index) => {
        if (/^\s+$/.test(part)) return part;
        const style = { "--word-index": wordIndex++ } as CSSProperties;
        return (
          <span className="pt-footer-word" style={style} key={`${part}-${index}`}>
            {part}
          </span>
        );
      })}
    </>
  );
}

function PhoneMetricRing({
  label,
  abbr,
  value,
  icon: Icon
}: {
  label: string;
  abbr: string;
  value: number;
  icon: typeof Activity;
}) {
  return (
    <div className="pt-phone-metric-ring">
      <svg className="pt-phone-metric-chart" viewBox="0 0 42 42" aria-hidden="true">
        <circle cx="21" cy="21" r="17" pathLength="100" />
        <circle cx="21" cy="21" r="17" pathLength="100" strokeDasharray={`${value} 100`} />
      </svg>
      <Icon className="pt-phone-metric-icon" size={12} strokeWidth={1.8} aria-hidden="true" />
      <span title={label}>{abbr}</span>
      <strong>
        <CountUpText value={String(value)} />
      </strong>
    </div>
  );
}

function LiveSessionRing({ value }: { value: number }) {
  return (
    <span className="pt-live-session-ring" aria-hidden="true">
      <svg viewBox="0 0 42 42">
        <circle cx="21" cy="21" r="17" pathLength="100" />
        <circle cx="21" cy="21" r="17" pathLength="100" strokeDasharray={`${value} 100`} />
      </svg>
      <strong>
        <CountUpText value={String(value)} />
      </strong>
    </span>
  );
}

function HomeMethodThread() {
  const reduced = usePrefersReducedMotion();
  const [step, setStep] = useState(reduced ? 4 : 0);

  useEffect(() => {
    if (reduced) {
      setStep(4);
      return;
    }

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setStep(4);
      return;
    }

    const targets: Array<[string, number]> = [
      [".pt-system-section", 1],
      [".pt-route-matrix", 2],
      [".pt-editorial-marquee", 3],
      [".pt-home-contact-bridge", 4]
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number((entry.target as HTMLElement).dataset.threadStep ?? 0);
          setStep((current) => Math.max(current, index));
        });
      },
      { rootMargin: "0px 0px -24% 0px", threshold: 0.12 }
    );

    targets.forEach(([selector, index]) => {
      const target = document.querySelector<HTMLElement>(selector);
      if (!target) return;
      target.dataset.threadStep = String(index);
      observer.observe(target);
    });

    return () => observer.disconnect();
  }, [reduced]);

  return (
    <div
      className="pt-method-thread"
      style={{ "--thread-progress": reduced ? 1 : step / 4 } as CSSProperties}
      data-thread-step={step}
      aria-hidden="true"
    >
      <svg viewBox="0 0 100 1000" preserveAspectRatio="none">
        <path className="pt-method-thread-shadow" d="M50 4 C20 130 74 238 48 360 C18 500 78 610 52 742 C36 834 62 910 50 996" pathLength={1} />
        <path className="pt-method-thread-line" d="M50 4 C20 130 74 238 48 360 C18 500 78 610 52 742 C36 834 62 910 50 996" pathLength={1} />
      </svg>
    </div>
  );
}

function JorgeSignature() {
  return (
    <svg className="pt-jorge-signature" viewBox="0 0 310 98" aria-hidden="true">
      <path
        d="M18 64 C48 20 62 20 54 56 C47 92 84 84 98 50 C112 18 132 25 118 62 C108 88 140 76 156 46 C172 18 190 26 176 58 C164 88 198 86 216 55 C230 31 248 31 240 56 C234 78 260 78 288 44"
        pathLength={1}
      />
      <path d="M74 84 C128 72 176 82 250 74" pathLength={1} />
    </svg>
  );
}

function MediaHudChips({
  items,
  className = ""
}: {
  items: Array<[string, string]>;
  className?: string;
}) {
  return (
    <div className={`pt-media-hud ${className}`.trim()} aria-hidden="true">
      {items.map(([label, value], index) => (
        <span key={`${label}-${value}`} style={{ "--hud-index": index } as CSSProperties}>
          <b>{label}</b>
          <strong>{value}</strong>
        </span>
      ))}
    </div>
  );
}

function WordByWord({ text }: { text: string }) {
  const parts = text.split(/(\s+)/);
  let wordIndex = 0;
  return (
    <>
      {parts.map((part, index) => {
        if (/^\s+$/.test(part)) return part;
        const style = { "--word-index": wordIndex++ } as CSSProperties;
        return (
          <span className="pt-word-by-word" style={style} key={`${part}-${index}`}>
            {part}
          </span>
        );
      })}
    </>
  );
}

function PhoneChronometer({ locale, reduced }: { locale: Locale; reduced: boolean }) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (reduced || typeof window === "undefined") return;
    const id = window.setInterval(() => setTick((current) => (current + 1) % 7), 1000);
    return () => window.clearInterval(id);
  }, [reduced]);

  const metrics =
    locale === "en"
      ? [
          { label: "Strength", abbr: "STR", value: 86, icon: Dumbbell },
          { label: "Mobility", abbr: "MOB", value: 64, icon: Footprints },
          { label: "Balance", abbr: "BAL", value: 92, icon: Waves }
        ]
      : [
          { label: "Fuerza", abbr: "FZA", value: 86, icon: Dumbbell },
          { label: "Movilidad", abbr: "MOV", value: 64, icon: Footprints },
          { label: "Equilibrio", abbr: "EQ", value: 92, icon: Waves }
        ];

  return (
    <div className="pt-phone-chrono" aria-hidden="true">
      <div className="pt-phone-chrono-main">
        <div className="pt-phone-chrono-time">
          <span>{locale === "en" ? "Active time" : "Tiempo activo"}</span>
          <strong>{formatTimer(reduced ? 252 : 252 + tick)}</strong>
        </div>
        <span className="pt-phone-chrono-bar">
          <i />
        </span>
      </div>
      <div className="pt-phone-metric-grid">
        {metrics.map((metric) => (
          <PhoneMetricRing key={metric.label} {...metric} />
        ))}
      </div>
    </div>
  );
}

function PhoneTrainingVideo({ locale, asset }: { locale: Locale; asset: PhoneLoopAsset }) {
  const reduced = usePrefersReducedMotion();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const slotRef = useRef<HTMLDivElement | null>(null);
  const [isInView, setIsInView] = useState(reduced);

  useEffect(() => {
    if (!reduced || !videoRef.current) return;
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
  }, [reduced]);

  useEffect(() => {
    if (reduced || !videoRef.current) return;
    const video = videoRef.current;
    video.muted = true;
    void video.play().catch(() => undefined);
  }, [asset.mp4, asset.webm, reduced]);

  useEffect(() => {
    if (reduced) {
      setIsInView(true);
      return;
    }

    if (typeof window === "undefined" || !slotRef.current || !("IntersectionObserver" in window)) {
      setIsInView(true);
      return;
    }

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsInView(true);
      },
      { threshold: 0.28 }
    );

    observer.observe(slotRef.current);
    return () => observer.disconnect();
  }, [reduced]);

  return (
    <div className={`pt-phone-video-slot ${isInView ? "is-inview" : ""}`} ref={slotRef}>
      <video
        ref={videoRef}
        className="pt-phone-loop-video"
        autoPlay={!reduced}
        muted
        loop
        playsInline
        preload="metadata"
        poster={withVersion(asset.poster)}
        aria-label={locale === "en" ? "Controlled training session inside the method interface" : "Sesión controlada dentro de la interfaz del método"}
      >
        <source src={withVersion(asset.webm)} type="video/webm" />
        <source src={withVersion(asset.mp4)} type="video/mp4" />
      </video>
      <PhoneChronometer locale={locale} reduced={reduced} />
    </div>
  );
}

function MethodPhoneInterface({ locale, variant = "portrait" }: { locale: Locale; variant?: "portrait" | "system" | "future" | "landscape" | "mini" }) {
  const isFuturePlan = variant === "future";
  const rows =
    locale === "en"
      ? [
          ["01", "Schedule and travel", true],
          ["02", "Training place", true],
          ["03", "Available equipment", false],
          ["04", "Experience", false],
          ["05", "Objective and limits", false]
        ]
      : [
          ["01", "Agenda y viajes", true],
          ["02", "Lugar de entrenamiento", true],
          ["03", "Material disponible", false],
          ["04", "Experiencia", false],
          ["05", "Objetivo y límites", false]
        ];
  const weeklyRows =
    locale === "en"
      ? [
          ["Mon", "Strength", true],
          ["Tue", "Mobility", true],
          ["Thu", "Balance", false],
          ["Fri", "Walk", false]
        ]
      : [
          ["Lun", "Fuerza", true],
          ["Mar", "Movilidad", true],
          ["Jue", "Equilibrio", false],
          ["Vie", "Caminar", false]
        ];
  const phoneLoop = variant === "system" || variant === "landscape" ? phoneLoops.seniorStrength : phoneLoops.adultMobility;
  const visibleRows = variant === "landscape" ? rows : variant === "system" ? rows.slice(0, 3) : rows.slice(0, 4);

  return (
    <div className={`pt-method-phone-ui pt-method-phone-ui-${variant}`} aria-hidden="true">
      <PhoneTrainingVideo locale={locale} asset={phoneLoop} />

      {isFuturePlan ? (
        <>
          <div className="pt-method-ui-topline">
            <span>9:41</span>
            <strong>{locale === "en" ? "This week" : "Esta semana"}</strong>
          </div>

          <div className="pt-method-ui-heading pt-method-ui-week-heading">
            <strong>{locale === "en" ? "Weekly plan" : "Plan semanal"}</strong>
            <span>{locale === "en" ? "Clear, adjustable." : "Claro, ajustable."}</span>
          </div>

          <div className="pt-method-ui-card pt-method-ui-week-card">
            <span>{locale === "en" ? "Training route" : "Ruta de entrenamiento"}</span>
            {weeklyRows.map(([day, label, done]) => (
              <p className={done ? "is-done" : ""} key={`${day}-${label}`}>
                <b>{day}</b>
                <span>{label}</span>
                <em>{done ? <CheckCircle2 size={14} /> : <i />}</em>
              </p>
            ))}
          </div>

          <div className="pt-method-ui-message pt-method-ui-whatsapp">
            <small>{locale === "en" ? "Team WhatsApp" : "WhatsApp del equipo"}</small>
            <p>{locale === "en" ? "We move mobility to Thursday. Keep strength today." : "Pasamos movilidad al jueves. Hoy mantén fuerza."}</p>
          </div>
        </>
      ) : (
        <>
          <div className="pt-method-ui-topline">
            <span>9:41</span>
            <strong>{locale === "en" ? "Start here" : "Empieza aquí"}</strong>
          </div>

          <div className="pt-method-ui-heading">
            <strong>{locale === "en" ? "Before the plan" : "Antes del plan"}</strong>
            <span>{locale === "en" ? "Only what you choose to share." : "Solo lo que decides contar."}</span>
          </div>

          <div className="pt-method-ui-card">
            <span>{locale === "en" ? "Questions to confirm" : "Preguntas a confirmar"}</span>
            {visibleRows.map(([number, label, done]) => (
              <p className={done ? "is-done" : ""} key={`${number}-${label}`}>
                <b>{number}</b>
                <span>{label}</span>
                <em>{done ? <CheckCircle2 size={14} /> : <i />}</em>
              </p>
            ))}
          </div>

          <div className="pt-method-ui-message">
            <small>{locale === "en" ? "Team review" : "Revisión del equipo"}</small>
            <p>{locale === "en" ? "Jorge and his team check the plan before making it harder." : "Jorge y su equipo revisan el plan antes de hacerlo más difícil."}</p>
          </div>

          <div className="pt-method-ui-status">
            <div className="pt-method-ui-badge">
              <strong>
                <CountUpText value="1:1" />
              </strong>
              <small>{locale === "en" ? "First talk" : "Primera charla"}</small>
            </div>
            <div>
              <p>
                <span>{locale === "en" ? "Route" : "Ruta"}</span>
                <strong>{locale === "en" ? "Confirmed" : "Confirmada"}</strong>
              </p>
              <p>
                <span>{locale === "en" ? "Plan" : "Plan"}</span>
                <strong>{locale === "en" ? "After we talk" : "Tras hablar"}</strong>
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function RouteLink({
  page,
  locale,
  navigate,
  className,
  children,
  onClick,
  ariaLabel
}: {
  page: PageKey;
  locale: Locale;
  navigate: (href: string) => void;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
  ariaLabel?: string;
}) {
  const href = paths[page][locale];
  return (
    <a
      href={href}
      className={className}
      aria-label={ariaLabel}
      onClick={(event) => {
        event.preventDefault();
        trackEvent("navigation_click", { page, locale });
        onClick?.();
        navigate(href);
      }}
    >
      {children}
    </a>
  );
}

function App() {
  const { route, navigate } = useRoute();
  const t = copy[route.locale];
  useRouteScrollReset(route);

  useEffect(() => {
    const content = getRouteSeo(route.locale, route.page);

    const href = `${SITE_ORIGIN}${paths[route.page][route.locale]}`;
    document.documentElement.lang = route.locale;
    document.title = content.title;
    setMeta('meta[name="description"]', content.description);
    setMeta('link[rel="canonical"]', href, "href");
    setMeta(
      'meta[name="robots"]',
      route.page === "app" ? "noindex,nofollow" : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
    );
    setMeta('meta[property="og:title"]', content.title);
    setMeta('meta[property="og:description"]', content.description);
    setMeta('meta[property="og:url"]', href);
    setMeta('meta[property="og:image"]', `${SITE_ORIGIN}${SOCIAL_IMAGE}`);
    setMeta(
      'meta[property="og:image:alt"]',
      route.locale === "en"
        ? "Personal training for everyday strength on the Costa del Sol"
        : "Entrenamiento personal para ganar fuerza en el día a día en la Costa del Sol"
    );
    setMeta('meta[name="twitter:title"]', content.title);
    setMeta('meta[name="twitter:description"]', content.description);
    setMeta('meta[name="twitter:image"]', `${SITE_ORIGIN}${SOCIAL_IMAGE}`);
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((node) => node.remove());
    (["en", "es"] as Locale[]).forEach((locale) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = locale;
      link.href = `${SITE_ORIGIN}${paths[route.page][locale]}`;
      document.head.appendChild(link);
    });
    const xDefault = document.createElement("link");
    xDefault.rel = "alternate";
    xDefault.hreflang = "x-default";
    xDefault.href = `${SITE_ORIGIN}${paths[route.page].en}`;
    document.head.appendChild(xDefault);
  }, [route, t]);

  useEffect(() => {
    const rails = Array.from(
      document.querySelectorAll<HTMLElement>(".pt-route-board, .pt-plan-ledger, .pt-lifestyle-track")
    );

    if (!rails.length) {
      return;
    }

    const normalizeWheelDelta = (event: WheelEvent) => {
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
        return event.deltaY * 16;
      }

      if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
        return event.deltaY * window.innerHeight;
      }

      return event.deltaY;
    };

    const forwardVerticalWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
        return;
      }

      event.preventDefault();
      window.scrollBy({ top: normalizeWheelDelta(event), behavior: "auto" });
    };

    rails.forEach((rail) => rail.addEventListener("wheel", forwardVerticalWheel, { passive: false }));

    return () => {
      rails.forEach((rail) => rail.removeEventListener("wheel", forwardVerticalWheel));
    };
  }, [route.page, route.locale]);

  useEffect(() => {
    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>(
        [
          ".pt-hero",
          ".pt-intro-strip",
          ".pt-director-section",
          ".pt-lifestyle-rail",
          ".pt-section",
          ".pt-home-credibility",
          ".pt-final-cta",
          ".pt-page",
          ".pt-page-hero",
          ".pt-system-steps article",
          ".pt-system-context-panel article",
          ".pt-system-signal-bar span",
          ".pt-system-coach-note",
          ".pt-first-timers",
          ".pt-first-timer-card",
          ".pt-comparison-stack article",
          ".pt-route-board article",
          ".pt-route-diagnostic span",
          ".pt-profile-builder",
          ".pt-profile-builder article",
          ".pt-editorial-claim",
          ".pt-editorial-marquee",
          ".pt-home-contact-bridge",
          ".pt-route-console",
          ".pt-home-route-card",
          ".pt-plan-card",
          ".pt-workout-card",
          ".pt-media-hud",
          ".pt-hud-card",
          ".pt-premium-proof-cards article",
          ".pt-home-steps article",
          ".pt-trust-ribbon span",
          ".pt-jorge-portrait",
          ".pt-jorge-gallery-item",
          ".pt-authority-pillars article",
          ".pt-authority-timeline article",
          ".pt-check-card",
          ".pt-check-side-panel article",
          ".pt-contact-visual",
          ".pt-contact-form",
          ".pt-simple-page .pt-page-hero",
          ".pt-footer"
        ].join(", ")
      )
    );

    // 2026-07-08 Job 80B: scroll reveals removed. Fast scrolling exposed the
    // hidden "curtain" state of sections on desktop, and the staggered
    // per-section transitions caused visible stutter on mobile. All content
    // is now always visible; only the hero keeps its one-time load-in.
    nodes.forEach((node) => node.classList.add("is-visible"));
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const hero = document.querySelector<HTMLElement>(".pt-hero");
      if (hero) hero.setAttribute("data-reveal", "pending");
    }
  }, [route.page, route.locale]);

  return (
    <>
      <a className="skip-link" href="#main">
        {route.locale === "en" ? "Skip to content" : "Saltar al contenido"}
      </a>
      <Header route={route} navigate={navigate} />
      <main id="main" className={`pt-site pt-site-${route.page} pt-locale-${route.locale}`}>
        {route.page === "home" ? (
          <HomePage locale={route.locale} navigate={navigate} />
        ) : route.page === "functional" ? (
          <FunctionalTrainingPage locale={route.locale} navigate={navigate} />
        ) : route.page === "method" ? (
          <MethodPage locale={route.locale} navigate={navigate} />
        ) : route.page === "plans" ? (
          <PlansPage locale={route.locale} navigate={navigate} />
        ) : route.page === "competition" ? (
          <CompetitionPage locale={route.locale} navigate={navigate} />
        ) : route.page === "jorge" ? (
          <JorgePage locale={route.locale} navigate={navigate} />
        ) : route.page === "check" ? (
          <CheckPage locale={route.locale} navigate={navigate} />
        ) : route.page === "contact" ? (
          <ContactPage locale={route.locale} />
        ) : (
          <SimplePage locale={route.locale} page={route.page as ContentPageKey} navigate={navigate} />
        )}
      </main>
      <Footer locale={route.locale} navigate={navigate} />
      <Consent locale={route.locale} page={route.page} />
      <MobileCtaBar route={route} navigate={navigate} />
    </>
  );
}

function MobileCtaBar({ route, navigate }: { route: RouteState; navigate: (href: string) => void }) {
  const t = copy[route.locale];

  return (
    <aside className="pt-mobile-cta-bar" aria-label={route.locale === "en" ? "Fast contact actions" : "Acciones rápidas de contacto"}>
      <div className="pt-mobile-system-status" aria-live="polite">
        <span aria-hidden="true" />
        <strong>{route.locale === "en" ? "Talk first" : "Primero hablar"}</strong>
        <small>1:1</small>
      </div>
      <RouteLink page="check" locale={route.locale} navigate={navigate} onClick={() => trackEvent("mobile_try_one_day_click", { locale: route.locale })}>
        {route.locale === "en" ? "Start here" : "Empezar"}
        <ArrowRight size={17} />
      </RouteLink>
      <RouteLink
        page="contact"
        locale={route.locale}
        navigate={navigate}
        ariaLabel={t.cta.consultation}
        onClick={() => trackEvent("mobile_consult_click", { locale: route.locale })}
      >
        {route.locale === "en" ? "Consult" : "Consulta"}
        <MessageCircle size={17} />
      </RouteLink>
    </aside>
  );
}

function Header({ route, navigate }: { route: RouteState; navigate: (href: string) => void }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const t = copy[route.locale];
  const altLocale: Locale = route.locale === "en" ? "es" : "en";
  const localeLabel = route.locale === "en" ? "Castellano" : "English";
  const mobileLocaleLabel = route.locale === "en" ? "Cambiar a castellano" : "Switch to English";
  const primaryNavKeys = navKeys.filter((key) => key !== "contact");
  const whatsappNavUrl = buildWhatsAppUrl(
    route.locale === "en"
      ? "Hi, I would like to ask about personal training."
      : "Hola, me gustaría preguntar por el entrenamiento personal."
  );

  useEffect(() => setOpen(false), [route.page, route.locale]);

  useEffect(() => {
    const sentinel = document.querySelector<HTMLElement>(".pt-header-scroll-sentinel");
    if (!sentinel || !("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setScrolled(!entry?.isIntersecting);
      },
      { rootMargin: "0px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <span className="pt-header-scroll-sentinel" aria-hidden="true" />
      <header className={`site-header pt-header${scrolled ? " is-scrolled" : ""}${route.page !== "home" ? " is-on-page" : ""}`}>
        <RouteLink page="home" locale={route.locale} navigate={navigate} className="brand-lockup pt-brand">
          <BrandMark />
          <span>
            <strong>{t.brand}</strong>
            <small>{t.byline}</small>
          </span>
        </RouteLink>

        <nav className="desktop-nav pt-nav" aria-label={route.locale === "en" ? "Primary navigation" : "Navegación principal"}>
          {primaryNavKeys.map((key) => (
            <RouteLink key={key} page={key} locale={route.locale} navigate={navigate} className={route.page === key ? "active" : ""}>
              {t.nav[key as keyof typeof t.nav]}
            </RouteLink>
          ))}
          <RouteLink page={route.page} locale={altLocale} navigate={navigate} className="pt-nav-locale">
            {localeLabel}
          </RouteLink>
          <a href={whatsappNavUrl} target="_blank" rel="noreferrer" onClick={() => trackEvent("nav_whatsapp_click", { locale: route.locale })}>
            {t.nav.contact}
          </a>
        </nav>

        <div className="header-actions">
          <RouteLink page={route.page} locale={altLocale} navigate={navigate} className="locale-switch">
            {localeLabel}
          </RouteLink>
          <RouteLink page="contact" locale={route.locale} navigate={navigate} className="header-cta pt-header-cta">
            {route.locale === "en" ? "Talk to us" : "Consulta"}
          </RouteLink>
          <button className="menu-button" type="button" aria-expanded={open} aria-controls="mobile-menu" onClick={() => setOpen((value) => !value)}>
            {open ? <X size={21} /> : <Menu size={21} />}
            <span className="sr-only">Menu</span>
          </button>
        </div>

        {open && (
          <nav id="mobile-menu" className="mobile-menu" aria-label={route.locale === "en" ? "Mobile navigation" : "Navegación móvil"}>
            {navKeys.map((key) => (
              key === "contact" ? (
                <a key={key} href={whatsappNavUrl} target="_blank" rel="noreferrer" onClick={() => trackEvent("mobile_nav_whatsapp_click", { locale: route.locale })}>
                  {t.nav[key as keyof typeof t.nav]}
                </a>
              ) : (
                <RouteLink key={key} page={key} locale={route.locale} navigate={navigate}>
                  {t.nav[key as keyof typeof t.nav]}
                </RouteLink>
              )
            ))}
            <RouteLink page={route.page} locale={altLocale} navigate={navigate} className="mobile-locale-switch">
              {mobileLocaleLabel}
            </RouteLink>
            <RouteLink page="contact" locale={route.locale} navigate={navigate} className="mobile-primary">
              {t.cta.consultation}
            </RouteLink>
          </nav>
        )}
      </header>
    </>
  );
}

function BrandGlyph() {
  return (
    <svg className="pt-brand-symbol" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
      <circle className="pt-symbol-field" cx="32" cy="32" r="26" />
      <path className="pt-symbol-accent" d="M18 40c7.8-10.6 19.5-10.6 28 0" />
      <path className="pt-symbol-line" d="M18 24h28" />
      <path className="pt-symbol-spark" d="M22 24v16M42 24v16" />
      <circle className="pt-symbol-dot" cx="32" cy="32" r="4.4" />
    </svg>
  );
}

function BrandMark() {
  return (
    <span className="brand-mark pt-brand-mark" aria-hidden="true">
      <span className="pt-brand-initials">JT</span>
    </span>
  );
}

function HomePage({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const t = copy[locale];
  const reduced = usePrefersReducedMotion();
  const heroLoop = heroLoops.homeCosta;
  return (
    <>
      <section className="pt-hero">
        <div className="pt-hero-media pt-reference-hero-media">
          {reduced ? (
            <StillImage
              src={heroLoop.poster}
              alt={locale === "en" ? "Adults training beside the Costa del Sol sea at sunset" : "Adultos entrenando junto al mar de la Costa del Sol al atardecer"}
              className="pt-reference-hero-image pt-home-hero-poster"
              loading="eager"
            />
          ) : (
            <video
              className="pt-reference-hero-image pt-home-hero-video"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster={withVersion(heroLoop.poster)}
              aria-label={locale === "en" ? "Adults training beside the Costa del Sol sea at sunset" : "Adultos entrenando junto al mar de la Costa del Sol al atardecer"}
            >
              <source src={withVersion(heroLoop.webm)} type="video/webm" />
              <source src={withVersion(heroLoop.mp4)} type="video/mp4" />
            </video>
          )}
        </div>
        <div className="pt-hero-content">
          <span className="pt-eyebrow">{t.hero.eyebrow}</span>
          <h1 className="pt-reference-hero-title">
            {locale === "en" ? (
              <>
                <span>Train to</span>
                {" "}
                <span>
                  <em>live better</em>
                </span>
              </>
            ) : (
              <>
                <span>Entrena</span>
                {" "}
                <span>para</span>
                {" "}
                <span>
                  <em>vivir mejor</em>
                </span>
              </>
            )}
          </h1>
          <p className="pt-hero-body">{t.hero.body}</p>
          <p className="pt-hero-proof">{t.hero.proof}</p>
          <div className="pt-actions">
            <RouteLink page="check" locale={locale} navigate={navigate} className="pt-button pt-button-primary">
              {t.cta.primary} <ArrowRight size={21} />
            </RouteLink>
            <RouteLink page="contact" locale={locale} navigate={navigate} className="pt-button pt-button-ghost">
              {locale === "en" ? "Consult" : "Consulta"} <MessageCircle size={19} />
            </RouteLink>
          </div>
          <SegmentSelector locale={locale} navigate={navigate} />
        </div>
        <HeroMetrics locale={locale} />
      </section>

      <HomeMethodThread />
      <HomePrivateSystem locale={locale} navigate={navigate} />
      <HomeFearReduction locale={locale} navigate={navigate} />
      <HomeComparisonModule locale={locale} navigate={navigate} />
      <HomeLifestyleRail locale={locale} navigate={navigate} />
      <HomeReviewsSection locale={locale} />
      <HomeRouteMatrix locale={locale} navigate={navigate} />
      <HomeEditorialMarquee locale={locale} />
      <HomeStepBarrier locale={locale} navigate={navigate} />
      <HomeContactBridge locale={locale} navigate={navigate} />
    </>
  );
}

function HomeStepBarrier({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const isEn = locale === "en";
  const points = isEn
    ? [
        ["No minimum level", "You come as you are. The first day is not a test to pass or fail."],
        ["No one is watching", "You train with Jorge, at your pace. No mirrors, no posing, no audience."],
        ["It is just a message", "You decide nothing today. Tell us how you are and we tell you where to start."]
      ]
    : [
        ["No hay nivel mínimo", "Vienes como estás. El primer día no se aprueba ni se suspende."],
        ["Nadie te mira", "Entrenas con Jorge, a tu ritmo. Sin espejos, sin postureo, sin público."],
        ["Solo es un mensaje", "Hoy no decides nada. Nos cuentas cómo estás y te decimos por dónde empezar."]
      ];

  return (
    <section className="pt-section pt-step-barrier">
      <div className="pt-step-barrier-copy">
        <span className="pt-eyebrow">{isEn ? "Taking the step" : "Dar el paso"}</span>
        <h2>{isEn ? "What if I feel embarrassed to start?" : "¿Y si me da vergüenza empezar?"}</h2>
        <p>
          {isEn
            ? "It is the most normal doubt in the world. Many people think it before writing: that they are not fit, that they start too late, that they will look silly. It is fine. Nobody here judges you. We are here to help you."
            : "Es la duda más normal del mundo. Mucha gente lo piensa antes de escribir: que si no está en forma, que si empieza tarde, que si va a hacer el ridículo. No pasa nada. Aquí nadie te juzga. Estamos para ayudarte."}
        </p>
        <div className="pt-actions">
          <a
            className="pt-button pt-button-primary"
            href={buildWhatsAppUrl(
              isEn
                ? "Hi Jorge, I would like to start but I feel a little unsure. Can you help me take the first step?"
                : "Hola Jorge, me gustaría empezar pero me da un poco de reparo. ¿Me ayudáis a dar el primer paso?"
            )}
            target="_blank"
            rel="noreferrer"
          >
            {isEn ? "Write us, no commitment" : "Escríbenos, sin compromiso"} <ArrowRight size={20} />
          </a>
        </div>
      </div>
      <ul className="pt-step-barrier-points">
        {points.map(([title, body]) => (
          <li key={title}>
            <strong>{title}</strong>
            <p>{body}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function HomeReviewsSection({ locale }: { locale: Locale }) {
  const reviews = realReviews.filter((review) => review.locale === locale && review.quote && review.author && review.sourceUrl);
  if (!reviews.length) return null;

  const sourceUrl = reviews[0]?.sourceUrl;
  const sourceLabel = locale === "en" ? "See reviews on Google" : "Ver reseñas en Google";

  return (
    <section className="pt-section pt-reviews-section" aria-label={locale === "en" ? "Verified client reviews" : "Reseñas verificadas de clientes"}>
      <div className="pt-section-copy">
        <span className="pt-eyebrow">{locale === "en" ? "Verified reviews" : "Reseñas verificadas"}</span>
        <h2>{locale === "en" ? "What clients say after training with the method." : "Lo que cuentan los clientes después de entrenar con el método."}</h2>
      </div>
      <div className="pt-reviews-grid">
        {reviews.slice(0, 3).map((review) => (
          <figure key={`${review.author}-${review.quote}`}>
            <div aria-label={`${review.rating} stars`}>{"★".repeat(Math.max(0, Math.min(5, Math.round(review.rating))))}</div>
            <blockquote>{review.quote}</blockquote>
            <figcaption>{review.author}</figcaption>
          </figure>
        ))}
      </div>
      {sourceUrl ? (
        <a className="pt-button pt-button-primary" href={sourceUrl} target="_blank" rel="noreferrer">
          {sourceLabel} <ArrowRight size={20} />
        </a>
      ) : null}
    </section>
  );
}

function HomePrivateSystem({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const isEn = locale === "en";
  const steps = isEn
    ? [
        ["01", "First chat", "We listen to how you feel, what you want and what worries you."],
        ["02", "Your week, made simple", "You know what to do each day and why it matters."],
        ["03", "We adjust with you", "If your body or your week changes, the plan changes too."]
      ]
    : [
        ["01", "Primera charla", "Escuchamos cómo estás, qué quieres y qué te preocupa."],
        ["02", "Tu semana, más sencilla", "Sabes qué hacer cada día y para qué sirve."],
        ["03", "Ajustamos contigo", "Si cambia tu cuerpo o tu semana, también cambia el plan."]
      ];
  const signals = isEn
    ? [["Your plan changes with you", "No fixed routine."], ["Clear every week", "Know what to do and why."], ["Team by WhatsApp", "You are not left alone."]]
    : [["Tu plan cambia contigo", "Sin rutina fija."], ["Claro cada semana", "Sabes qué hacer y por qué."], ["Equipo por WhatsApp", "No te dejamos solo."]];
  const noteSignals = isEn
    ? [["Effort", "Right level"], ["Energy", "Checked"], ["Movement", "Improved"]]
    : [["Esfuerzo", "A tu nivel"], ["Energía", "Revisada"], ["Movimiento", "Mejorado"]];
  const contextMoments = isEn
    ? [
        [CalendarDays, "Schedule and travel", "Your week, made simple"],
        [MapPin, "Training place", "Your time, place and equipment decide how we start."],
        [Dumbbell, "Available equipment", "No fixed routine."]
      ]
    : [
        [CalendarDays, "Agenda y viajes", "Tu semana, más sencilla"],
        [MapPin, "Lugar de entrenamiento", "Tu tiempo, lugar y material deciden cómo empezamos."],
        [Dumbbell, "Material disponible", "Sin rutina fija."]
      ];

  return (
    <section className="pt-director-section pt-system-section">
      <div className="pt-system-copy">
        <span className="pt-section-index">01</span>
        <h2>{isEn ? "We see how you are and give you a simple plan to follow." : "Vemos cómo estás y te damos un plan fácil de seguir."}</h2>
        <p>
          {isEn
            ? "The idea is simple: we learn how you live, how you move and what worries you. Then we build training that helps, without forcing you."
            : "La idea es sencilla: vemos cómo vives, cómo te mueves y qué te cuesta. Después montamos un entrenamiento que ayude, sin forzarte."}
        </p>
        <div className="pt-system-steps">
          {steps.map(([number, title, body]) => (
            <article key={title}>
              <strong>
                <CountUpStepNumber value={number} />
              </strong>
              <div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            </article>
          ))}
        </div>
        <RouteLink page="check" locale={locale} navigate={navigate} className="pt-director-link pt-button-primary">
          {copy[locale].cta.primary}
          <ArrowRight size={21} />
        </RouteLink>
      </div>

      <div className="pt-system-visual" aria-label={isEn ? "Jorge method preview" : "Vista del método de Jorge"}>
        <div className="pt-system-phone">
          <span className="pt-system-notch" />
          <MethodPhoneInterface locale={locale} variant="system" />
        </div>
        <aside className="pt-system-context-panel" aria-label={isEn ? "Starting context moments" : "Momentos de contexto inicial"}>
          <span>{isEn ? "Before the plan" : "Antes del plan"}</span>
          <strong>{isEn ? "Only what you choose to share." : "Solo lo que decides contar."}</strong>
          <div>
            {contextMoments.map(([Icon, title, body]) => {
              const MomentIcon = Icon as typeof CalendarDays;
              return (
                <article key={title as string}>
                  <MomentIcon size={21} />
                  <div>
                    <b>{title as string}</b>
                    <p>{body as string}</p>
                  </div>
                </article>
              );
            })}
          </div>
          <RouteLink page="check" locale={locale} navigate={navigate} className="pt-system-context-cta">
            {copy[locale].cta.primary}
            <ArrowRight size={18} />
          </RouteLink>
        </aside>
        <div className="pt-system-coach-note" aria-label={isEn ? "How the team decides" : "Cómo decide el equipo"}>
          <span className="pt-note-kicker">{isEn ? "What the team sees" : "Lo que mira el equipo"}</span>
          <strong>
            {isEn
              ? "If something is not working, we change it in time."
              : "Si algo no va bien, lo cambiamos a tiempo."}
          </strong>
          <p>
            {isEn
              ? "You do not need more data. The team checks what matters and keeps the plan simple."
              : "No hacen falta más datos. El equipo revisa lo importante y mantiene el plan simple."}
          </p>
          <div className="pt-note-grid">
            {noteSignals.map(([label, value]) => (
              <span key={label}>
                <b>{label}</b>
                <em>{value}</em>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="pt-system-signal-bar">
        {signals.map(([title, body], index) => {
          const icons = [CalendarDays, ClipboardCheck, MessageCircle];
          const Icon = icons[index];
          return (
            <span key={title}>
              <Icon size={25} />
              <strong>{title}</strong>
              {body}
            </span>
          );
        })}
      </div>
    </section>
  );
}

function HomeRouteMatrix({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const isEn = locale === "en";
  const routes = isEn
    ? [
        ["01", "Home Training", "Costa del Sol", "Jorge comes to your home. No mirrors, no stares, no posing. Strength and balance with someone who never judges.", "From 90 EUR / session"],
        ["02", "Annual Premium", "For the whole year", "A whole year with support. Consistency does your body good, and your head too.", "Ask our team"],
        ["03", "Online Coaching", "From anywhere", "Your weekly plan wherever you are. The team checks your progress and helps you keep going.", "100 EUR / month"],
        ["04", "Technique Review", "Plan plus technique", "Your online plan plus in-person sessions to fix posture and move better.", "60 EUR / session"]
      ]
    : [
        ["01", "Entrenamiento en casa", "Costa del Sol", "Jorge va a tu casa. Sin espejos, sin miradas, sin postureo. Fuerza y equilibrio con alguien que no te juzga.", "Desde 90 EUR / sesión"],
        ["02", "Premium Anual", "Para todo el año", "Un año entero acompañado. Al cuerpo le sienta bien la constancia, y a la cabeza también.", "Consulta con nuestro equipo"],
        ["03", "Entrenamiento Online", "Desde cualquier lugar", "Tu plan semanal estés donde estés. El equipo revisa cómo vas y te ayuda a no dejarlo.", "100 EUR / mes"],
        ["04", "Revisión de Técnica", "Plan más técnica", "Tu plan online y sesiones en persona para corregir la postura y moverte mejor.", "60 EUR / sesión"]
      ];
  const diagnostic = isEn
    ? [["How you are now", "What you tell us before the first session."], ["Your week", "Time, place, equipment and support."], ["What you want", "Strength, confidence, doing more or sport."]]
    : [["Cómo estás ahora", "Lo que nos cuentas antes de la primera sesión."], ["Tu semana", "Tiempo, zona, material y apoyo."], ["Qué quieres", "Fuerza, confianza, hacer más cosas o deporte."]];
  const profileBlocks = isEn
    ? [
        [CalendarDays, "Your week", "Two days free. One trip soon."],
        [Dumbbell, "Your equipment", "Gym access and dumbbells at home."],
        [Activity, "Your experience", "You move a little, but want help."],
        [BadgeCheck, "Your goal", "More strength for daily life."]
      ]
    : [
        [CalendarDays, "Tu semana", "Dos días libres. Un viaje pronto."],
        [Dumbbell, "Tu material", "Gimnasio y mancuernas en casa."],
        [Activity, "Tu experiencia", "Te mueves algo, pero quieres ayuda."],
        [BadgeCheck, "Tu objetivo", "Más fuerza para el día a día."]
      ];

  return (
    <section className="pt-director-section pt-route-matrix">
      <div className="pt-route-intro">
        <span className="pt-section-index">03</span>
        <h2>{isEn ? "There is a way of training that fits your life." : "Hay una forma de entrenar que encaja con tu vida."}</h2>
        <p>{copy[locale].plans.body}</p>
        <blockquote>
          {isEn
            ? "Tell us what you need. We design the plan and adjust it with you each week."
            : "Nos cuentas qué necesitas. Diseñamos el plan y lo ajustamos contigo cada semana."}
          <span>Jorge Tardío</span>
        </blockquote>
        <aside className="pt-profile-builder" aria-label={isEn ? "Example training profile" : "Ficha de ejemplo"}>
          <span>{isEn ? "Example profile" : "Ficha de ejemplo"}</span>
          <strong>{isEn ? "We build from your real week." : "Partimos de tu semana real."}</strong>
          <div>
            {profileBlocks.map(([Icon, title, body]) => {
              const ProfileIcon = Icon as typeof CalendarDays;
              return (
                <article key={title as string}>
                  <ProfileIcon size={20} />
                  <div>
                    <b>{title as string}</b>
                    <p>{body as string}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </aside>
      </div>

      <div className="pt-route-board">
        <div className="pt-route-board-head">
          <span>{isEn ? "Route" : "Ruta"}</span>
          <span>{isEn ? "Good for" : "Ideal para"}</span>
          <span>{isEn ? "Investment" : "Inversión"}</span>
          <span>{isEn ? "Next step" : "Siguiente paso"}</span>
        </div>
        {routes.map(([number, name, signal, body, price]) => (
          <article key={name}>
            <strong>{number}</strong>
            <div>
              <h3>{name}</h3>
              <small>{signal}</small>
            </div>
            <p>{body}</p>
            <b>
              <CountUpPrice value={price} />
            </b>
            <RouteLink page="check" locale={locale} navigate={navigate}>
              {isEn ? "Start here" : "Empieza aquí"} <ArrowRight size={18} />
            </RouteLink>
          </article>
        ))}
      </div>
      <div className="pt-route-editorial-indicators" aria-hidden="true">
        {routes.map(([number, name], index) => (
          <span className={index === 0 ? "is-active" : ""} key={`${number}-${name}`}>
            {number}
          </span>
        ))}
      </div>

      <div className="pt-route-diagnostic">
        {diagnostic.map(([title, body], index) => (
          <span key={title}>
            <b>{String(index + 1).padStart(2, "0")}</b>
            <strong>{title}</strong>
            {body}
          </span>
        ))}
        <i aria-hidden="true">=</i>
        <strong>{isEn ? "Start improving your life" : "Empieza a mejorar tu vida"}</strong>
      </div>
    </section>
  );
}

function HomeLifestyleRail({
  locale,
  navigate,
  ctaPage = "functional"
}: {
  locale: Locale;
  navigate: (href: string) => void;
  ctaPage?: PageKey;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const beltRef = useRef<HTMLDivElement>(null);
  const isEn = locale === "en";
  const checklist = isEn
    ? [
        ["Golf rotation", "Hips + thoracic control"],
        ["Home strength", "Dumbbells or bodyweight"],
        ["Balance work", "Confidence beyond training"]
      ]
    : [
        ["Rotación de golf", "Cadera y control torácico"],
        ["Fuerza en casa", "Mancuernas o peso corporal"],
        ["Equilibrio", "Confianza en el día a día"]
      ];
  const photoCards = isEn
    ? [
        {
          src: images.workoutDiverseTraining,
          className: "is-diverse-card",
          title: "Simple corrections",
          body: "Clear cues so you know how to move and feel safe.",
          alt: "Adults training with supervision"
        },
        {
          src: images.workoutSeaBalance,
          className: "is-sea-card",
          title: "Balance for real life",
          body: "Strength and stability for walks, trips, stairs and the routines you want to keep.",
          alt: "Coastal balance training"
        },
        {
          src: images.workoutGolf,
          className: "is-golf-card",
          title: "Keep doing what you enjoy",
          body: "Strength and mobility for golf, sea, trips and an active life on the Costa del Sol.",
          alt: "Adult golfer training power and rotation on the Costa del Sol"
        }
      ]
    : [
        {
          src: images.workoutDiverseTraining,
          className: "is-diverse-card",
          title: "Correcciones simples",
          body: "Indicaciones claras para saber cómo moverte y sentirte seguro.",
          alt: "Adultos entrenando con supervisión"
        },
        {
          src: images.workoutSeaBalance,
          className: "is-sea-card",
          title: "Equilibrio para tu vida",
          body: "Fuerza y estabilidad para caminar, viajar, subir escaleras y seguir con tus rutinas.",
          alt: "Entrenamiento de equilibrio en la costa"
        },
        {
          src: images.workoutGolf,
          className: "is-golf-card",
          title: "Sigue haciendo lo que disfrutas",
          body: "Fuerza y movilidad para golf, mar, viajes y una vida activa en la Costa del Sol.",
          alt: "Golfista adulto entrenando potencia y rotación en la Costa del Sol"
        }
      ];

  // CSS drives the continuous motion. This effect only gives the keyframe the
  // belt's real pixel distance because WebKit resolves max-content percentages
  // against the viewport-sized box instead of the belt's full scroll width.

  useEffect(() => {
    const belt = beltRef.current;
    const track = trackRef.current;

    if (!belt || !track) return;

    let frame = 0;
    let active = true;
    const images = Array.from(belt.querySelectorAll("img"));

    const syncDistance = () => {
      if (!active) return;

      const distance = belt.scrollWidth / 2;
      if (distance <= 0) return;

      belt.style.setProperty("--pt-belt-distance", `${-distance}px`);
      belt.dataset.motionReady = "true";
    };

    const scheduleSync = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = window.requestAnimationFrame(syncDistance);
      });
    };

    const resizeObserver = new ResizeObserver(scheduleSync);
    resizeObserver.observe(track);
    belt.querySelectorAll<HTMLElement>(".pt-workout-card").forEach((card) => resizeObserver.observe(card));
    images.forEach((image) => {
      if (!image.complete) image.addEventListener("load", scheduleSync);
    });
    window.addEventListener("resize", scheduleSync);
    void document.fonts.ready.then(scheduleSync);
    scheduleSync();

    return () => {
      active = false;
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      images.forEach((image) => image.removeEventListener("load", scheduleSync));
      window.removeEventListener("resize", scheduleSync);
    };
  }, [locale]);

  const moveRail = (direction: -1 | 1) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * Math.min(window.innerWidth * 0.72, 760), behavior: "smooth" });
    trackEvent("workout_carousel_scroll", { locale, direction });
  };

  return (
    <section id="workouts" className="pt-lifestyle-rail pt-workout-carousel" aria-label={isEn ? "Workout examples carousel" : "Carrusel de ejemplos de entrenamiento"}>
      <div className="pt-lifestyle-rail-copy">
        <div>
          <span className="pt-eyebrow">{isEn ? "Exercise for real life" : "Ejercicio para la vida real"}</span>
          <h2>{isEn ? "Train for the life you want." : "Entrena para la vida que quieres."}</h2>
        </div>
        <div className="pt-workout-copy-panel">
          <p>
            {isEn
              ? "You do not need to love the gym. Start with a plan that helps you feel stronger, safer and able to do more."
              : "No tienes que amar el gimnasio. Empieza con un plan que te ayude a sentirte más fuerte, más seguro y capaz de hacer más."}
          </p>
          <RouteLink page={ctaPage} locale={locale} navigate={navigate} className="pt-workout-section-cta">
            {isEn ? "Start moving better" : "Empezar a moverme mejor"} <ArrowRight size={18} />
          </RouteLink>
          <div className="pt-workout-controls" aria-label={isEn ? "Carousel controls" : "Controles del carrusel"}>
            <button type="button" onClick={() => moveRail(-1)} aria-label={isEn ? "Previous example" : "Ejemplo anterior"}>
              <ArrowRight size={21} />
            </button>
            <button type="button" onClick={() => moveRail(1)} aria-label={isEn ? "Next example" : "Siguiente ejemplo"}>
              <ArrowRight size={21} />
            </button>
          </div>
        </div>
      </div>
      <div className="pt-lifestyle-track" ref={trackRef} tabIndex={0} aria-label={isEn ? "Workout examples" : "Ejemplos de entrenamiento"}>
        <div className="pt-lifestyle-belt" ref={beltRef}>
        {([0, 1] as const).map((loopIndex) => (
          <Fragment key={`workout-loop-${loopIndex}`}>
            <article className="pt-workout-card pt-future-card pt-future-card-photo" aria-hidden={loopIndex === 1 ? "true" : undefined}>
              <StillImage src={images.workoutPrivateTerrace} alt={isEn ? "Coaching session adapted to the client's week" : "Sesión de entrenamiento adaptada a la semana del cliente"} loading="eager" />
              <div className="pt-future-photo-copy">
                <h3>{isEn ? "Your week comes first" : "Tu semana va primero"}</h3>
                <p>{isEn ? "Your time, place and equipment decide how we start." : "Tu tiempo, lugar y material deciden cómo empezamos."}</p>
              </div>
              <div className="pt-future-checklist" aria-label={isEn ? "Confirmed training context" : "Contexto de entrenamiento confirmado"}>
                {checklist.map(([title, body]) => (
                  <span key={title}>
                    <b>
                      {title}
                      <small>{body}</small>
                    </b>
                    <CheckCircle2 size={25} />
                  </span>
                ))}
              </div>
            </article>

            <article className="pt-workout-card pt-future-card pt-future-card-hud-media" aria-hidden={loopIndex === 1 ? "true" : undefined}>
              <StillImage src={images.heroReferenceBattle} alt={isEn ? "Controlled strength work with coaching support" : "Trabajo de fuerza controlado con apoyo del equipo"} loading="eager" />
              <div className="pt-future-card-copy">
                <h3>{isEn ? "Move with confidence" : "Muévete con confianza"}</h3>
                <p>
                  {isEn
                    ? "Clear exercises, simple cues and progress without feeling like another app."
                    : "Ejercicios claros, instrucciones simples y progreso sin sentir que es otra app."}
                </p>
              </div>
              <MediaHudChips
                items={isEn ? [["Effort", "Right level"], ["Energy", "Checked"], ["Route", "Updated"]] : [["Esfuerzo", "A tu nivel"], ["Energía", "Revisada"], ["Ruta", "Actualizada"]]}
              />
            </article>

            {photoCards.map((card) => (
              <article key={`${loopIndex}-${card.src}`} className={`pt-workout-card pt-future-card pt-future-card-photo pt-future-card-single ${card.className}`} aria-hidden={loopIndex === 1 ? "true" : undefined}>
                <StillImage src={card.src} alt={card.alt} loading="eager" />
                <div className="pt-future-photo-copy">
                  <h3>{card.title}</h3>
                  <p>{card.body}</p>
                </div>
              </article>
            ))}
          </Fragment>
        ))}
        </div>
      </div>
    </section>
  );
}

function HomeFearReduction({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const isEn = locale === "en";
  const routes = isEn
    ? [
        ["01", "Getting started", "We start with the basics, close guidance and at your pace."],
        ["02", "Medium", "You already move. We add order and strength to your week."],
        ["03", "Advanced", "You already know how to train. We help you level up."]
      ]
    : [
        ["01", "Iniciación", "Empezamos con lo básico, con guía cercana y a tu ritmo."],
        ["02", "Medio", "Ya te mueves. Ponemos orden y fuerza a tu semana."],
        ["03", "Avanzado", "Ya sabes entrenar. Te ayudamos a subir de nivel."]
      ];

  return (
    <section className="pt-director-section pt-first-timers">
      <div className="pt-first-timers-copy">
        <span className="pt-section-index">02</span>
        <h2>{isEn ? "You do not need to know how to train." : "No necesitas saber entrenar."}</h2>
        <p>
          {isEn
            ? "You only need to tell us how you are. We choose the first step with you."
            : "Solo tienes que contarnos cómo estás. Elegimos el primer paso contigo."}
        </p>
      </div>
      <div className="pt-first-timers-routes">
        {routes.map(([number, title, body]) => (
          <RouteLink page="check" locale={locale} navigate={navigate} className="pt-first-timer-card" key={title}>
            <span>{number}</span>
            <strong>{title}</strong>
            <p>{body}</p>
            <i>
              {isEn ? "Start here" : "Empieza aquí"} <ArrowRight size={17} />
            </i>
          </RouteLink>
        ))}
      </div>
    </section>
  );
}

function SegmentSelector({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const items =
    locale === "en"
      ? [
          ["More strength day to day", "check" as PageKey],
          ["Feel safer moving", "functional" as PageKey],
          ["A bigger challenge", "competition" as PageKey]
        ]
      : [
          ["Más fuerza en el día a día", "check" as PageKey],
          ["Moverme con más seguridad", "functional" as PageKey],
          ["Un reto más grande", "competition" as PageKey]
        ];
  return (
    <div className="pt-segment-selector" aria-label={locale === "en" ? "Choose your starting point" : "Elige tu punto de partida"}>
      <strong>{locale === "en" ? "I want" : "Quiero"}</strong>
      <div>
        {items.map(([label, page]) => (
          <RouteLink key={label} page={page as PageKey} locale={locale} navigate={navigate}>
            {label}
          </RouteLink>
        ))}
      </div>
    </div>
  );
}

function HomeComparisonModule({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const t = copy[locale].comparison;
  return (
    <section className="pt-section pt-comparison-module">
      <div className="pt-section-copy">
        <span className="pt-eyebrow">{locale === "en" ? "People before apps" : "Personas antes que apps"}</span>
        <h2>{t.title}</h2>
        <p>{t.body}</p>
        <RouteLink page="check" locale={locale} navigate={navigate} className="pt-button pt-button-primary">
          {copy[locale].cta.primary} <ArrowRight size={20} />
        </RouteLink>
      </div>
      <div className="pt-comparison-stack">
        {t.rows.map(([title, body], index) => (
          <article key={title} className={index === 1 ? "is-active" : ""}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <div>
              <strong>{title}</strong>
              <p>{body}</p>
            </div>
            <i aria-hidden="true" />
          </article>
        ))}
      </div>
    </section>
  );
}

function HomePremiumPositioning({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const items =
    locale === "en"
      ? [
          [BadgeCheck, "People before software", "Technology helps, but Jorge and his team choose what is right for you."],
          [MapPin, "Made for your week", "At home, online or in person, the plan fits your time, travel and privacy."],
          [ClipboardCheck, "Start without pressure", "First we meet, understand how you are and choose the safest way to begin."]
        ]
      : [
          [BadgeCheck, "Personas antes que software", "La tecnología ayuda, pero Jorge y su equipo eligen qué es mejor para ti."],
          [MapPin, "Pensado para tu semana", "En casa, online o en persona, el plan encaja con tu tiempo, viajes y privacidad."],
          [ClipboardCheck, "Empieza sin presión", "Primero nos conocemos, vemos cómo estás y elegimos cómo empezar con más seguridad."]
        ];

  const signals =
    locale === "en"
      ? ["For your rhythm", "Bilingual", "WhatsApp", "Clear next step"]
      : ["A tu ritmo", "Bilingüe", "WhatsApp", "Paso claro"];

  return (
    <section className="pt-section pt-premium-positioning">
      <div className="pt-premium-positioning-copy">
        <span className="pt-eyebrow">{locale === "en" ? "Clear and personal" : "Claro y personal"}</span>
        <h2>
          {locale === "en"
            ? "Good coaching feels calm because it is personal."
            : "Un buen entrenamiento se siente claro porque es personal."}
        </h2>
        <p>
          {locale === "en"
            ? "No crowded classes. No app that promises to fix everything. No pressure before it makes sense. People review the plan with you."
            : "Sin clases masivas. Sin prometer que una app lo arregla todo. Sin presión antes de verlo claro. Personas revisan el plan contigo."}
        </p>
        <div className="pt-signal-row">
          {signals.map((signal) => (
            <span key={signal}>{signal}</span>
          ))}
        </div>
        <RouteLink page="contact" locale={locale} navigate={navigate} className="pt-button pt-button-primary">
          {copy[locale].cta.consultation} <ArrowRight size={20} />
        </RouteLink>
      </div>

      <div className="pt-premium-proof-stack" aria-label={locale === "en" ? "Strategic service evidence" : "Evidencia estratégica del servicio"}>
        <div className="pt-premium-proof-video">
          <StillImage
            src={images.refLifestyleSled}
            alt={locale === "en" ? "Athlete driving a sled in a strength session" : "Atleta empujando trineo en sesión de fuerza"}
            className="pt-premium-proof-image"
          />
          <MediaHudChips
            className="pt-media-hud-dark"
            items={
              locale === "en"
                ? [["Effort", "Right level"], ["Energy", "Checked"]]
                : [["Esfuerzo", "A tu nivel"], ["Energía", "Revisada"]]
            }
          />
        </div>
        <div className="pt-premium-proof-cards">
          {items.map(([Icon, title, body], index) => {
            const ProofIcon = Icon as typeof BadgeCheck;
            return (
              <article key={title as string}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <ProofIcon size={25} />
                <strong>{title as string}</strong>
                <p>{body as string}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HeroMetrics({ locale }: { locale: Locale }) {
  const reduced = usePrefersReducedMotion();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (reduced || typeof window === "undefined") return;
    const id = window.setInterval(() => setTick((current) => (current + 1) % 7), 1000);
    return () => window.clearInterval(id);
  }, [reduced]);

  const metrics = [86, 64, 92];

  return (
    <div className="pt-live-session-bar" aria-label={locale === "en" ? "Live session progress" : "Progreso de sesión en directo"}>
      <div className="pt-live-session-state">
        <span className="pt-live-session-dot" aria-hidden="true" />
        <strong>{locale === "en" ? "Live session · Marbella" : "En sesión · Marbella"}</strong>
      </div>
      <div className="pt-live-session-time">
        <span>{locale === "en" ? "Active time" : "Tiempo activo"}</span>
        <strong>{formatTimer(reduced ? 252 : 252 + tick)}</strong>
      </div>
      <div className="pt-live-session-rings">
        {metrics.map((value) => (
          <LiveSessionRing key={value} value={value} />
        ))}
      </div>
    </div>
  );
}

function HomeMethodPreview({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const t = copy[locale];
  const previewSteps = [t.method.steps[0], t.method.steps[2], t.method.steps[3]];
  const stats =
    locale === "en"
      ? [["Any age", "Start where you are"], ["Today", "Talk first"], ["Every week", "Plan updated"]]
      : [["Cualquier edad", "Empiezas donde estás"], ["Hoy", "Primero hablar"], ["Cada semana", "Plan actualizado"]];

  return (
    <section className="pt-section pt-home-method-preview">
      <div className="pt-section-copy">
        <span className="pt-eyebrow">{locale === "en" ? "How we begin" : "Cómo empezamos"}</span>
        <h2>{locale === "en" ? "First we understand you. Then we build the plan." : "Primero te entendemos. Después montamos el plan."}</h2>
        <p>
          {locale === "en"
            ? "The next step is simple. Tell us what you need. Start calmly. We adjust the plan with you each week."
            : "El siguiente paso es simple. Nos cuentas qué necesitas. Empiezas con calma. Ajustamos el plan contigo cada semana."}
        </p>
      </div>

      <div className="pt-home-method-grid">
        <div className="pt-home-steps">
          {previewSteps.map(([title, body], index) => (
            <article key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <div>
                <strong>{title}</strong>
                <p>{body}</p>
              </div>
            </article>
          ))}
          <div className="pt-actions">
            <RouteLink page="method" locale={locale} navigate={navigate} className="pt-button pt-button-primary">
              {t.cta.method} <ArrowRight size={20} />
            </RouteLink>
            <RouteLink page="check" locale={locale} navigate={navigate} className="pt-button pt-button-primary">
              {t.cta.primary} <ArrowRight size={20} />
            </RouteLink>
          </div>
        </div>

        <div className="pt-home-device-card">
          <LandscapeDevice locale={locale} mode="method-ui" title={locale === "en" ? "Jorge method UI preview" : "Vista de la UI del método de Jorge"} />
          <div className="pt-home-stat-row">
            {stats.map(([stat, label]) => (
              <span key={label}>
                <strong>
                  <CountUpText value={stat} />
                </strong>
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function HomeRoutesPreview({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const t = copy[locale];
  const routeIcons = [Home, CalendarDays, UserCheck];
  const routeNotes =
    locale === "en"
      ? ["Good if you want to start calmly at home.", "Good if you want support all year.", "Good if online support is enough for you."]
      : ["Ideal si quieres empezar con calma en casa.", "Ideal si quieres apoyo todo el año.", "Ideal si el apoyo online es suficiente para ti."];
  const routeSignals = locale === "en" ? ["Train at home", "Annual", "Online"] : ["Domicilio", "Anual", "Online"];

  return (
    <section className="pt-section pt-home-routes-preview">
      <div className="pt-section-copy pt-centered-copy">
        <span className="pt-eyebrow">{locale === "en" ? "Ways to train" : "Formas de entrenar"}</span>
        <h2>{locale === "en" ? "One service, three ways to start." : "Un servicio, tres formas de empezar."}</h2>
        <p>
          {locale === "en"
            ? "Home, online and in-person review are three ways to make training fit your week."
            : "Casa, online o revisión presencial son tres formas de hacer que el entrenamiento encaje en tu semana."}
        </p>
      </div>

      <div className="pt-route-system-panel" aria-label={locale === "en" ? "Route decision system" : "Sistema de decisión de rutas"}>
        <div className="pt-route-console">
          <span>{locale === "en" ? "Where to begin" : "Por dónde empezar"}</span>
          <strong>{locale === "en" ? "We listen. We choose. We train." : "Escuchamos. Elegimos. Entrenamos."}</strong>
          <div className="pt-route-signal-bars" aria-hidden="true">
            <i />
            <i />
            <i />
            <i />
          </div>
          <p>
            {locale === "en"
            ? "The right route fits your confidence, equipment, place and goal."
            : "La ruta correcta encaja con tu confianza, material, lugar y objetivo."}
          </p>
        </div>
        <div className="pt-route-command-list">
        {t.plans.cards.slice(0, 3).map(([name, price], index) => {
          const Icon = routeIcons[index];
          return (
            <RouteLink page="plans" locale={locale} navigate={navigate} className="pt-home-route-card" key={name}>
              <div className="pt-home-route-copy">
                <small>{routeSignals[index]}</small>
                <Icon size={30} />
                <h3>{name}</h3>
                <p>{routeNotes[index]}</p>
                <strong>
                  <CountUpPrice value={price} />
                </strong>
              </div>
              <ArrowRight size={23} />
            </RouteLink>
          );
        })}
        </div>
      </div>

      <div className="pt-home-routes-action">
        <RouteLink page="plans" locale={locale} navigate={navigate} className="pt-button pt-button-primary">
          {t.cta.secondary} <ArrowRight size={20} />
        </RouteLink>
        <RouteLink page="competition" locale={locale} navigate={navigate} className="pt-button pt-button-outline">
          {t.cta.competition} <ArrowRight size={20} />
        </RouteLink>
      </div>
    </section>
  );
}

function HomeCredibilityStrip({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const items =
    locale === "en"
      ? [
          ["40+ years", "Helping people train better"],
          ["2002-2024", "National trophies and titles"],
          ["Arnold", "Arnold Classic participation"]
        ]
      : [
          ["40+ años", "Ayudando a entrenar mejor"],
          ["2002-2024", "Trofeos y campeonatos nacionales"],
          ["Arnold", "Participación en Arnold Classic"]
        ];

  return (
    <section className="pt-home-credibility" aria-label={locale === "en" ? "Jorge credibility preview" : "Resumen de credibilidad de Jorge"}>
      <div>
        <span className="pt-eyebrow">{locale === "en" ? "Experience that helps" : "Experiencia que ayuda"}</span>
        <h2>{locale === "en" ? "Experience that helps you train with more confidence." : "Experiencia para entrenar con más confianza."}</h2>
      </div>
      <div className="pt-home-credibility-stats">
        {items.map(([stat, label]) => (
          <span key={label}>
            <strong>
              <CountUpText value={stat} />
            </strong>
            {label}
          </span>
        ))}
      </div>
      <RouteLink page="jorge" locale={locale} navigate={navigate} className="pt-button pt-button-light">
        {copy[locale].cta.jorge} <ArrowRight size={20} />
      </RouteLink>
    </section>
  );
}

function HomeEditorialMarquee({ locale }: { locale: Locale }) {
  const promise = copy[locale].footer.promise;
  const repeated = Array.from({ length: 5 }, (_, index) => `${promise}${index === 4 ? "" : " "}`);
  return (
    <section className="pt-editorial-marquee" aria-label={locale === "en" ? "Training promise" : "Promesa de entrenamiento"}>
      <div>
        <p>
          {repeated.map((item, index) => (
            <span key={`${item}-${index}`}>{item}</span>
          ))}
        </p>
        <p aria-hidden="true">
          {repeated.map((item, index) => (
            <span key={`${item}-clone-${index}`}>{item}</span>
          ))}
        </p>
      </div>
    </section>
  );
}

function HomeContactBridge({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const isEn = locale === "en";
  return (
    <section className="pt-home-contact-bridge">
      <div>
        <span className="pt-eyebrow">{isEn ? "Next step" : "Siguiente paso"}</span>
        <h2>{isEn ? "Shall we look at it together?" : "¿Lo vemos juntos?"}</h2>
        <p>{isEn ? "Send a WhatsApp. We tell you where to start." : "Manda un WhatsApp. Te decimos por dónde empezar."}</p>
      </div>
      <RouteLink page="contact" locale={locale} navigate={navigate} className="pt-button pt-button-primary">
        {copy[locale].cta.contact} <ArrowRight size={20} />
      </RouteLink>
    </section>
  );
}

function HomeEditorialClaim({ locale }: { locale: Locale }) {
  const words = copy[locale].footer.promise.split(" ");
  return (
    <section className="pt-editorial-claim" aria-label={locale === "en" ? "Training promise" : "Promesa de entrenamiento"}>
      <p>
        {words.map((word, index) => (
          <span key={`${word}-${index}`} style={{ "--claim-word": index } as CSSProperties}>
            {word}
          </span>
        ))}
      </p>
    </section>
  );
}

function MethodSection({
  locale,
  navigate,
  cta = "method"
}: {
  locale: Locale;
  navigate: (href: string) => void;
  cta?: "method" | "check";
}) {
  const t = copy[locale];
  const ctaPage = cta === "check" ? "check" : "method";
  const ctaText = cta === "check" ? t.cta.primary : t.cta.method;
  return (
    <section className="pt-section pt-method-section">
      <div className="pt-section-copy">
        <span className="pt-eyebrow">{locale === "en" ? "The Jorge Tardío Method" : "Método Jorge Tardío"}</span>
        <h1>{t.method.title}</h1>
        <p>{t.method.body}</p>
      </div>

      <div className="pt-method-grid">
        <div className="pt-method-flow">
          {t.method.steps.map(([title, body], index) => (
            <article key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <div>
                <strong>{title}</strong>
                <p>{body}</p>
              </div>
            </article>
          ))}
          <RouteLink page={ctaPage} locale={locale} navigate={navigate} className="pt-button pt-button-primary">
            {ctaText} <ArrowRight size={20} />
          </RouteLink>
        </div>

        <div className="pt-device-stage">
          <LandscapeDevice locale={locale} mode="method-ui" title={locale === "en" ? "Jorge method UI preview" : "Vista de la UI del método de Jorge"} />
          <div className="pt-method-stats">
            {t.method.stats.map(([stat, label]) => (
              <span key={label}>
                <strong>
                  <CountUpText value={stat} />
                </strong>
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PlansSection({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const t = copy[locale];
  const icons = [Home, BarChart3, CalendarDays, UserCheck];
  const principles = locale === "en"
    ? [["01", "First we talk"], ["02", "Choose the simplest start"], ["03", "Adjust every week"], ["04", "Go further when you are ready"]]
    : [["01", "Primero hablamos"], ["02", "Elegir el inicio más simple"], ["03", "Ajustar cada semana"], ["04", "Ir a más cuando estés listo"]];
  return (
    <section className="pt-section pt-plans-section pt-plans-editorial">
      <div className="pt-section-copy pt-centered-copy">
        <span className="pt-eyebrow">{locale === "en" ? "Ways to train" : "Formas de entrenar"}</span>
        <h1>{t.plans.title}</h1>
        <p>{t.plans.body}</p>
      </div>

      <div className="pt-plans-editorial-layout">
        <aside className="pt-plans-principle" aria-label={locale === "en" ? "How plans are selected" : "Cómo se eligen los planes"}>
          <span className="pt-principle-index">01-04</span>
          <h2>{locale === "en" ? "Four ways to start. Same care." : "Cuatro formas de empezar. El mismo cuidado."}</h2>
          <p>
            {locale === "en"
            ? "Plans are simple ways to start. We choose them from your body, confidence, time and support."
            : "Los planes son formas sencillas de empezar. Los elegimos según tu cuerpo, confianza, tiempo y apoyo."}
          </p>
          <div className="pt-plan-principle-grid">
            {principles.map(([number, text]) => (
              <span key={number}>
                <b>{number}</b>
                {text}
              </span>
            ))}
          </div>
        </aside>

        <div className="pt-plan-ledger">
        {t.plans.cards.map(([name, price, detail, signal], index) => {
          const Icon = icons[index];
          return (
            <article className={index === 0 ? "is-priority" : ""} key={name}>
              <span className="pt-plan-number">{String(index + 1).padStart(2, "0")}</span>
              <div className="pt-plan-symbol" aria-hidden="true">
                <Icon size={34} />
              </div>
              <div className="pt-plan-body">
                <small>{signal}</small>
                <h3>{name}</h3>
                <p>{detail}</p>
              </div>
              <strong>
                <CountUpPrice value={price} />
              </strong>
              <RouteLink page="check" locale={locale} navigate={navigate} className="pt-plan-action">
                {locale === "en" ? "Start here" : "Empieza aquí"} <ArrowRight size={19} />
              </RouteLink>
            </article>
          );
        })}
        </div>
        <div className="pt-plan-editorial-indicators" aria-hidden="true">
          {t.plans.cards.map(([name], index) => (
            <span className={index === 0 ? "is-active" : ""} key={name}>
              {String(index + 1).padStart(2, "0")}
            </span>
          ))}
        </div>
      </div>
      <TrustRibbon locale={locale} />
    </section>
  );
}

function CompetitionSection({ locale, navigate, compact = false }: { locale: Locale; navigate: (href: string) => void; compact?: boolean }) {
  const t = copy[locale];
  const stats = locale === "en"
    ? [["40+", "Years training"], ["2002-2024", "National titles"], ["Arnold", "Classic participation"]]
    : [["40+", "años entrenando"], ["2002-2024", "títulos nacionales"], ["Arnold", "Classic participación"]];

  return (
    <section className={`pt-section pt-competition-section ${compact ? "pt-competition-compact" : ""}`}>
      <div className="pt-competition-visual">
        <img
          src="/assets/gallery/ptcs-bikini-03.jpg"
          loading="lazy"
          sizes="(max-width: 700px) 45vw, 22vw"
          alt={locale === "en" ? "Bikini-fitness competitor training with a kettlebell on the Costa del Sol" : "Competidora bikini fitness entrenando con kettlebell en la Costa del Sol"}
        />
        <img
          src="/assets/gallery/ptcs-bodybuilder-01.jpg"
          loading="lazy"
          sizes="(max-width: 700px) 45vw, 22vw"
          alt={locale === "en" ? "Male bodybuilder training with dumbbells on the Costa del Sol seafront" : "Culturista entrenando con mancuernas en el paseo marítimo de la Costa del Sol"}
        />
      </div>
      <div className="pt-competition-copy">
        <span className="pt-eyebrow">{locale === "en" ? "For a bigger challenge" : "Para un reto mayor"}</span>
        <h1>
          <WordByWord text={t.competition.title} />
        </h1>
        <p>{t.competition.body}</p>
        <div className="pt-discipline-list">
          {t.competition.disciplines.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <div className="pt-competition-stats" aria-label={locale === "en" ? "Competition highlights" : "Datos de competición"}>
          {stats.map(([stat, label]) => (
            <span key={stat}>
              <strong>
                <CountUpText value={stat} />
              </strong>
              <small>{label}</small>
            </span>
          ))}
        </div>
        <p className="pt-competition-note">{t.competition.note}</p>
        <div className="pt-actions">
          <RouteLink page="check" locale={locale} navigate={navigate} className="pt-button pt-button-light">
            {t.cta.competition} <ArrowRight size={20} />
          </RouteLink>
          <RouteLink page="contact" locale={locale} navigate={navigate} className="pt-button pt-button-line-light">
            {t.cta.contact}
          </RouteLink>
        </div>
      </div>
    </section>
  );
}

function TrustRibbon({ locale }: { locale: Locale }) {
  const items =
    locale === "en"
      ? [
          [ShieldCheck, "Your information stays private"],
          [MessageCircle, "English and Spanish support by WhatsApp"],
          [CheckCircle2, "Clear rules before you start"],
          [UserCheck, "People decide, not a machine"]
        ]
      : [
          [ShieldCheck, "Tu información queda privada"],
          [MessageCircle, "Soporte en inglés y español por WhatsApp"],
          [CheckCircle2, "Reglas claras antes de empezar"],
          [UserCheck, "Deciden personas, no una máquina"]
        ];
  return (
    <div className="pt-trust-ribbon">
      {items.map(([Icon, text]) => {
        const TrustIcon = Icon as typeof ShieldCheck;
        return (
          <span key={text as string}>
            <TrustIcon size={26} />
            {text as string}
          </span>
        );
      })}
    </div>
  );
}

function LandscapeDevice({
  locale,
  visual,
  title,
  mode = "media"
}: {
  locale: Locale;
  visual?: VideoAsset | string;
  title: string;
  mode?: "media" | "method-ui";
}) {
  return (
    <div className="pt-landscape-device" aria-label={title}>
      <span className="pt-device-notch" />
      <i className="pt-device-button left" />
      <i className="pt-device-button right" />
      <div className="pt-device-screen">
        {mode === "method-ui" || !visual ? (
          <MethodPhoneInterface locale={locale} variant="landscape" />
        ) : typeof visual === "string" ? (
          <StillImage src={visual} alt={title} className="pt-device-image" loading="eager" />
        ) : (
          <MotionVideo visual={visual} label={title} eager />
        )}
      </div>
    </div>
  );
}

function MethodPage({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  return (
    <section className="pt-page pt-page-method pt-page-direct">
      <MethodSection locale={locale} navigate={navigate} cta="check" />
      <FinalCta locale={locale} navigate={navigate} />
    </section>
  );
}

function FunctionalTrainingPage({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const isEn = locale === "en";
  const outcomes = isEn
    ? [
        ["Strength", "Carry shopping, get up with confidence and make everyday effort feel easier."],
        ["Mobility", "Keep the movement you need for travel, golf, walks and the routines you enjoy."],
        ["Balance", "Feel steadier on your feet and safer when the ground or pace changes."],
        ["Energy", "Build a training habit that fits your week instead of taking it over."]
      ]
    : [
        ["Fuerza", "Carga la compra, levántate con confianza y haz que el esfuerzo diario resulte más llevadero."],
        ["Movilidad", "Conserva el movimiento que necesitas para viajar, jugar al golf, caminar y disfrutar de tus rutinas."],
        ["Equilibrio", "Siéntete más estable y seguro cuando cambia el terreno o el ritmo."],
        ["Energía", "Construye un hábito de entrenamiento que encaja en tu semana en lugar de dominarla."]
      ];

  return (
    <section className="pt-page pt-functional-page">
      <div className="pt-page-hero pt-functional-hero">
        <span className="pt-eyebrow">{isEn ? "Functional training for real life" : "Entrenamiento funcional para la vida real"}</span>
        <h1>
          {isEn ? "Functional training on the Costa del Sol." : "Entrenamiento funcional en la Costa del Sol."}
        </h1>
        <p>
          {isEn
            ? "Train to gain strength, mobility, balance and confidence for the life you actually live. The plan starts at your level and adapts to your time, place and equipment."
            : "Entrena para ganar fuerza, movilidad, equilibrio y confianza en la vida que realmente llevas. El plan parte de tu nivel y se adapta a tu tiempo, lugar y material."}
        </p>
        <div className="pt-actions">
          <RouteLink page="check" locale={locale} navigate={navigate} className="pt-button pt-button-primary">
            {isEn ? "Tell us where you are now" : "Cuéntanos cómo estás"} <ArrowRight size={20} />
          </RouteLink>
          <RouteLink page="method" locale={locale} navigate={navigate} className="pt-button pt-button-outline">
            {isEn ? "See the method" : "Ver el método"}
          </RouteLink>
        </div>
      </div>

      <section className="pt-section pt-functional-outcomes" aria-labelledby="functional-outcomes-title">
        <div className="pt-section-copy">
          <span className="pt-eyebrow">{isEn ? "What we train" : "Qué entrenamos"}</span>
          <h2 id="functional-outcomes-title">
            {isEn ? "Capacity that matters beyond the workout." : "Capacidad que importa fuera del entrenamiento."}
          </h2>
          <p>
            {isEn
              ? "There is no minimum fitness level and no need for previous gym experience. Beginner, medium and advanced routes all start from a real assessment."
              : "No hay nivel físico mínimo ni necesitas experiencia previa en un gimnasio. Iniciación, nivel medio y avanzado parten de una evaluación real."}
          </p>
        </div>
        <ul className="pt-functional-outcome-grid">
          {outcomes.map(([title, body], index) => (
            <li key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <strong>{title}</strong>
              <p>{body}</p>
            </li>
          ))}
        </ul>
      </section>

      <HomeLifestyleRail locale={locale} navigate={navigate} ctaPage="check" />
      <HomeFearReduction locale={locale} navigate={navigate} />
      <HomePrivateSystem locale={locale} navigate={navigate} />
      <FinalCta locale={locale} navigate={navigate} />
    </section>
  );
}

function PlansPage({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  return (
    <section className="pt-page pt-page-plans pt-page-direct">
      <PlansSection locale={locale} navigate={navigate} />
      <FinalCta locale={locale} navigate={navigate} />
    </section>
  );
}

function CompetitionGallery({ locale }: { locale: Locale }) {
  const isEn = locale === "en";
  const groups = [
    {
      label: isEn ? "Competition" : "Competición",
      note: isEn ? "Bikini fitness" : "Bikini fitness",
      items: [
        { src: "/assets/gallery/ptcs-bikini-01.jpg", wide: true, alt: isEn ? "Bikini-fitness athlete battle-rope training by the sea" : "Atleta bikini fitness entrenando con cuerda junto al mar" },
        { src: "/assets/gallery/ptcs-bikini-03.jpg", alt: isEn ? "Bikini-fitness athlete kettlebell squat on a coastal terrace" : "Atleta bikini fitness en sentadilla con kettlebell en terraza costera" },
        { src: "/assets/gallery/ptcs-bikini-05.jpg", wide: true, alt: isEn ? "Bikini-fitness athlete walking the shoreline" : "Atleta bikini fitness paseando por la orilla" }
      ]
    },
    {
      label: isEn ? "Bodybuilding" : "Culturismo",
      note: isEn ? "Serious muscle" : "Músculo de verdad",
      items: [
        { src: "/assets/gallery/ptcs-bodybuilder-01.jpg", alt: isEn ? "Male bodybuilder dumbbell training by the sea" : "Culturista entrenando con mancuernas junto al mar" },
        { src: "/assets/gallery/ptcs-bodybuilder-02.jpg", alt: isEn ? "Male bodybuilder barbell overhead press on the promenade" : "Culturista con press de barra sobre la cabeza en el paseo" },
        { src: "/assets/gallery/ptcs-bodybuilder-03.jpg", wide: true, alt: isEn ? "Male bodybuilder on the beach" : "Culturista en la playa" }
      ]
    },
    {
      label: isEn ? "Athletic" : "Atlético",
      note: isEn ? "Not a bodybuilder" : "Sin ser culturista",
      items: [
        { src: "/assets/gallery/ptcs-athletic-01.jpg", alt: isEn ? "Lean athletic man battle-rope training by the sea" : "Hombre atlético entrenando con cuerda junto al mar" },
        { src: "/assets/gallery/ptcs-athletic-02.jpg", alt: isEn ? "Athletic man suspension-strap training on a terrace" : "Hombre atlético entrenando con correas de suspensión en terraza" },
        { src: "/assets/gallery/ptcs-athletic-03.jpg", wide: true, alt: isEn ? "Athletic man kettlebell swing by the sea" : "Hombre atlético con swing de kettlebell junto al mar" }
      ]
    }
  ];

  return (
    <section className="pt-section pt-advanced-gallery" aria-label={isEn ? "Advanced training gallery" : "Galería de entrenamiento avanzado"}>
      <div className="pt-advanced-gallery-head">
        <span className="pt-eyebrow">{isEn ? "The advanced level, in pictures" : "El nivel avanzado, en imágenes"}</span>
        <h2>{isEn ? "Competition, bodybuilding and athletic performance." : "Competición, culturismo y rendimiento atlético."}</h2>
      </div>
      {groups.map((group) => (
        <div className="pt-advanced-gallery-group" key={group.label}>
          <div className="pt-advanced-gallery-label">
            <strong>{group.label}</strong>
            <span>{group.note}</span>
          </div>
          <div className="pt-advanced-gallery-grid">
            {group.items.map((item) => (
              <figure key={item.src} className={"wide" in item && item.wide ? "pt-advanced-gallery-item is-wide" : "pt-advanced-gallery-item"}>
                <img src={item.src} loading="lazy" alt={item.alt} />
              </figure>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function CompetitionPage({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  return (
    <section className="pt-page">
      <CompetitionSection locale={locale} navigate={navigate} />
      <CompetitionGallery locale={locale} />
      <FinalCta locale={locale} navigate={navigate} />
    </section>
  );
}

function JorgePage({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const t = copy[locale];
  const authority =
    locale === "en"
      ? {
          eyebrow: "Train with confidence",
          title: "More strength for a better life.",
          lead:
            "If training feels hard to start, the plan must be simple and calm. We help you live better, enjoy more and move with more confidence.",
          pillars: [
            {
              stat: "40+",
              title: "Years helping people train",
              body:
                "I have trained people of many ages and goals. We start with your life, not a perfect plan."
            },
            {
              stat: "Clear",
              title: "Build confidence",
              body:
                "You learn what to do, how to do it and why. Training becomes easy to understand."
            },
            {
              stat: "2002-2024",
              title: "National competition record",
              body:
                "From 2002 to 2024 I won multiple national trophies and bodybuilding championships, and took part in Arnold Classic."
            }
          ],
          sourceNote: "The trophy list is not the point. That experience helps you start with more confidence.",
          sourceBadges: ["40+ years coaching", "National titles 2002-2024", "Arnold Classic", "Costa del Sol"],
          proofTitle: "Training that helps you enjoy life.",
          proofBody:
            "Good training helps you climb stairs, carry bags, travel and enjoy sport. It helps you feel safer in your body.",
          timeline: [
            ["40+", "Years helping people train"],
            ["2002-2024", "Multiple national trophies and championships won"],
            ["Arnold", "Arnold Classic participation"]
          ],
          venues:
            "My background has one goal here: help you feel stronger from the first step."
        }
      : {
          eyebrow: "Entrenar con confianza",
          title: "Más fuerza para vivir mejor.",
          lead:
            "Si entrenar te cuesta empezar, el plan debe ser simple y tranquilo. Te ayudamos a vivir mejor, disfrutar más y moverte con más confianza.",
          pillars: [
            {
              stat: "40+",
              title: "Años ayudando a entrenar",
              body:
                "He entrenado a personas de muchas edades y objetivos. Empezamos con tu vida, no con un plan perfecto."
            },
            {
              stat: "Claro",
              title: "Ganar confianza",
              body:
                "Aprendes qué hacer, cómo hacerlo y por qué. Entrenar empieza a ser fácil de entender."
            },
            {
              stat: "2002-2024",
              title: "Trayectoria nacional",
              body:
                "De 2002 a 2024 gané múltiples trofeos y campeonatos nacionales de culturismo, y participé en Arnold Classic."
            }
          ],
          sourceNote: "La lista de copas no es lo importante. Esa experiencia te ayuda a empezar con más confianza.",
          sourceBadges: ["40+ años de experiencia", "Trofeos nacionales 2002-2024", "Arnold Classic", "Costa del Sol"],
          proofTitle: "Entrenar para disfrutar la vida.",
          proofBody:
            "Un buen entrenamiento te ayuda a subir escaleras, cargar bolsas, viajar y disfrutar del deporte. También te ayuda a sentirte más seguro con tu cuerpo.",
          timeline: [
            ["40+", "Años ayudando a entrenar"],
            ["2002-2024", "Múltiples trofeos y campeonatos nacionales"],
            ["Arnold", "Participación en Arnold Classic"]
          ],
          venues:
            "Mi trayectoria aquí tiene un objetivo: que te sientas más fuerte desde el primer paso."
        };

  return (
    <section className="pt-page pt-jorge-page">
      <div className="pt-jorge-hero">
        <div>
          <span className="pt-eyebrow">{locale === "en" ? "Jorge Tardío Borrego" : "Jorge Tardío Borrego"}</span>
          <h1>{t.jorge.title}</h1>
          <p className="pt-lead">{t.jorge.subtitle}</p>
          <p>{t.jorge.intro}</p>
          <div className="pt-actions">
            <RouteLink page="check" locale={locale} navigate={navigate} className="pt-button pt-button-primary">
              {t.cta.primary} <ArrowRight size={20} />
            </RouteLink>
            <RouteLink page="contact" locale={locale} navigate={navigate} className="pt-button pt-button-outline">
              {t.cta.contact}
            </RouteLink>
          </div>
        </div>
        <figure className="pt-jorge-portrait">
          <img src={images.jorge} alt={locale === "en" ? "Stage portrait of Jorge Tardío Borrego" : "Retrato de Jorge Tardío Borrego"} />
          <JorgeSignature />
          <figcaption>
            <strong>Jorge Tardío Borrego</strong>
            <span>{locale === "en" ? "Personal trainer · Costa del Sol" : "Entrenador personal · Costa del Sol"}</span>
          </figcaption>
        </figure>
      </div>
      <div className="pt-jorge-story">
        <p>{t.jorge.body}</p>
        <p>{t.jorge.body2}</p>
        <p>{t.jorge.body3}</p>
        <p>{t.jorge.role}</p>
      </div>
      <JorgeGallery locale={locale} />
      <div className="pt-jorge-authority">
        <div className="pt-authority-editorial">
          <span className="pt-eyebrow">{authority.eyebrow}</span>
          <h2>{authority.title}</h2>
          <p>{authority.lead}</p>
          <div className="pt-authority-source" aria-label={locale === "en" ? "Credential sources" : "Fuentes de credenciales"}>
            <p>{authority.sourceNote}</p>
            <div>
              {authority.sourceBadges.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
          <div className="pt-authority-pillars">
            {authority.pillars.map((item) => (
              <article key={item.title}>
                <strong className={item.stat.includes("-") ? "pt-stat-wide" : undefined}>
                  <CountUpText value={item.stat} />
                </strong>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </div>
              </article>
            ))}
          </div>
          <div className="pt-authority-note">
            <BadgeCheck size={28} />
            <div>
              <h3>{authority.proofTitle}</h3>
              <p>{authority.proofBody}</p>
            </div>
          </div>
        </div>
        <aside className="pt-authority-timeline" aria-label={locale === "en" ? "Competitive record" : "Trayectoria competitiva"}>
          <p>{authority.venues}</p>
          <div>
            {authority.timeline.map(([year, detail]) => (
              <article key={`${year}-${detail}`}>
                <strong className={year.includes("-") ? "pt-stat-wide" : undefined}>
                  <CountUpText value={year} />
                </strong>
                <span>{detail}</span>
              </article>
            ))}
          </div>
        </aside>
      </div>
    </section>
  );
}

function JorgeGallery({ locale }: { locale: Locale }) {
  const isEn = locale === "en";

  return (
    <section className="pt-jorge-gallery" aria-label={isEn ? "Personal training archive" : "Archivo personal de entrenamiento"}>
      <div className="pt-jorge-gallery-head">
        <div>
          <span className="pt-eyebrow">{isEn ? "My training archive" : "Mi archivo de entrenamiento"}</span>
          <h2>{isEn ? "Training room and competition." : "Sala de entrenamiento y competición."}</h2>
        </div>
        <p>
          {isEn
            ? "These images show my years of training before I help you start."
            : "Estas imágenes muestran mis años de entrenamiento antes de ayudarte a empezar."}
        </p>
      </div>
      <div className="pt-jorge-gallery-grid">
        {jorgeGallery.map((item) => (
          <figure key={item.src} className="pt-jorge-gallery-item">
            <StillImage src={item.src} alt={item.alt[locale]} />
            <figcaption>{item.caption[locale]}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function CheckPage({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const t = copy[locale];
  const languageOptions = locale === "en" ? ["English", "Spanish"] : ["Español", "Inglés"];
  const ageOptions =
    locale === "en"
      ? ["Getting started", "Medium", "Advanced"]
      : ["Iniciación", "Medio", "Avanzado"];
  const locationOptions =
    locale === "en"
      ? ["Marbella / Mijas", "Benalmadena / Fuengirola", "Estepona / Malaga", "Online only"]
      : ["Marbella / Mijas", "Benalmádena / Fuengirola", "Estepona / Málaga", "Solo online"];
  const serviceOptions =
    locale === "en"
      ? ["Train at home", "Support all year", "Online plan", "Online plan plus technique"]
      : ["Entrenar en casa", "Apoyo todo el año", "Plan online", "Plan online más técnica"];
  const goalOptions =
    locale === "en"
      ? ["More strength day to day", "Move with more confidence", "Build confidence", "A bigger challenge"]
      : ["Más fuerza en el día a día", "Moverme con más seguridad", "Ganar confianza", "Un reto más grande"];
  const contactOptions = ["WhatsApp"];
  const [answers, setAnswers] = useState({
    language: languageOptions[0],
    age: ageOptions[0],
    location: locationOptions[0],
    service: serviceOptions[0],
    goal: goalOptions[0],
    contact: contactOptions[0]
  });
  const [submitted, setSubmitted] = useState(false);

  const update = (key: keyof typeof answers, value: string) => {
    setAnswers((current) => ({ ...current, [key]: value }));
    setSubmitted(false);
  };

  const groups: Array<[keyof typeof answers, string, string[]]> = [
    ["language", locale === "en" ? "Language" : "Idioma", languageOptions],
    ["location", locale === "en" ? "Where can you train?" : "¿Dónde puedes entrenar?", locationOptions],
    ["age", locale === "en" ? "How you are now" : "Cómo estás ahora", ageOptions],
    ["service", locale === "en" ? "What help do you prefer?" : "¿Qué ayuda prefieres?", serviceOptions],
    ["goal", locale === "en" ? "What do you want?" : "Qué quieres", goalOptions]
  ];

  const contactChannel = "WhatsApp";

  const result =
    locale === "en"
      ? `Great. These answers prepare your ${contactChannel} conversation about ${answers.service} in ${answers.location}. Nothing to pay, no commitment.`
      : `Perfecto. Estas respuestas preparan tu conversación por ${contactChannel} sobre ${answers.service} en ${answers.location}. Sin pagar nada y sin compromiso.`;
  const preparedMessage =
    locale === "en"
      ? `Hi Jorge, I would like help choosing how to start.\n\nLanguage: ${answers.language}\nLocation: ${answers.location}\nCurrent level: ${answers.age}\nPreferred format: ${answers.service}\nGoal: ${answers.goal}`
      : `Hola Jorge, me gustaría que me ayudarais a elegir cómo empezar.\n\nIdioma: ${answers.language}\nUbicación: ${answers.location}\nNivel actual: ${answers.age}\nModalidad preferida: ${answers.service}\nObjetivo: ${answers.goal}`;

  return (
    <section className="pt-page pt-check-page">
      <div className="pt-page-hero">
        <span className="pt-eyebrow">{locale === "en" ? "Start here" : "Empieza aquí"}</span>
        <h1>{locale === "en" ? "Tell us how you want to feel." : "Cuéntanos cómo quieres sentirte."}</h1>
        <p>
          {locale === "en"
            ? "Tell us where you are now. We help you choose the easiest way to start. No pressure."
            : "Nos cuentas cómo estás. Te ayudamos a elegir la forma más sencilla de empezar. Sin presión."}
        </p>
      </div>
      <div className="pt-check-shell">
        <div className="pt-check-card">
          <h2>{locale === "en" ? "Where to start" : "Por dónde empezar"}</h2>
          <div className="pt-check-groups">
            {groups.map(([key, title, options]) => (
              <fieldset key={key}>
                <legend>{title}</legend>
                <div className="pt-choice-row">
                  {options.map((option) => (
                    <button key={option} type="button" aria-pressed={answers[key] === option} onClick={() => update(key, option)}>
                      {option}
                    </button>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
          <div className="pt-check-result">
            <CheckCircle2 size={23} />
            <span>{result}</span>
          </div>
          <a
            className="pt-button pt-button-outline"
            href={buildWhatsAppUrl(preparedMessage)}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              trackEvent("first_step_whatsapp_prepared", { locale, ...answers });
              setSubmitted(true);
            }}
          >
            {locale === "en" ? "Prepare WhatsApp conversation" : "Preparar conversación en WhatsApp"} <MessageCircle size={20} />
          </a>
          {submitted && (
            <div className="pt-check-confirmation">
              <strong>{locale === "en" ? "Best first step" : "Mejor primer paso"}</strong>
              <p>
                {locale === "en"
                  ? "WhatsApp opens with your answers ready. We confirm the route, area and easiest way to start before you decide."
                  : "WhatsApp se abre con tus respuestas preparadas. Confirmamos la ruta, la zona y la forma más sencilla de empezar antes de decidir."}
              </p>
            </div>
          )}
          <RouteLink page="contact" locale={locale} navigate={navigate} className="pt-button pt-button-primary">
            {t.cta.contact} <ArrowRight size={20} />
          </RouteLink>
        </div>
        <aside className="pt-check-side-panel" aria-label={locale === "en" ? "What happens next" : "Qué pasa después"}>
          {(locale === "en"
            ? [
                [ClipboardCheck, "How you are now", "We understand your life before choosing a plan."],
                [MapPin, "Where and how", "Home, online or in person depends on your week."],
                [Activity, "What you want", "Strength, balance, body shape or sport need different starts."],
                [MessageCircle, "WhatsApp follow-up", "We turn your answers into a simple WhatsApp chat."]
              ]
            : [
                [ClipboardCheck, "Cómo estás ahora", "Entendemos tu vida antes de elegir plan."],
                [MapPin, "Dónde y cómo", "Domicilio, online o presencial depende de tu semana."],
                [Activity, "Qué quieres", "Fuerza, equilibrio, físico o deporte necesitan inicios distintos."],
                [MessageCircle, "Seguimiento por WhatsApp", "Pasamos tus respuestas a una conversación sencilla por WhatsApp."]
              ]
          ).map(([Icon, title, body], index) => {
            const TypedIcon = Icon as typeof Activity;
            return (
              <article key={title as string}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <TypedIcon size={24} />
                <div>
                  <strong>{title as string}</strong>
                  <p>{body as string}</p>
                </div>
                <i aria-hidden="true" />
              </article>
            );
          })}
        </aside>
      </div>
    </section>
  );
}

function ContactPage({ locale }: { locale: Locale }) {
  const [sent, setSent] = useState(false);
  const t = copy[locale];

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const body =
      locale === "en"
        ? `Personal Trainer Costa del Sol enquiry\nName: ${form.get("name") ?? ""}\n\n${form.get("message") ?? t.contact.prompt}`
        : `Consulta Personal Trainer Costa del Sol\nNombre: ${form.get("name") ?? ""}\n\n${form.get("message") ?? t.contact.prompt}`;
    setSent(true);
    trackEvent("whatsapp_contact_start", { locale });
    window.location.href = buildWhatsAppUrl(body);
  };

  return (
    <section className="pt-page pt-contact-page">
      <div className="pt-contact-visual">
        <StillImage src={images.workoutSeaBalance} alt={locale === "en" ? "Calm paddleboarding on the Costa del Sol coast" : "Paddle surf tranquilo en la Costa del Sol"} className="pt-contact-image" />
        <div>
          <MessageCircle size={34} />
          <strong>{locale === "en" ? "Fast, clear and close" : "Rápido, claro y cercano"}</strong>
          <span>{locale === "en" ? "WhatsApp first. No pressure." : "Primero WhatsApp. Sin presión."}</span>
        </div>
      </div>
      <form className="pt-contact-form" onSubmit={onSubmit}>
        <span className="pt-eyebrow">{locale === "en" ? "Contact" : "Contacto"}</span>
        <h1>{t.contact.title}</h1>
        <p>{t.contact.body}</p>
        <label>
          <span>{t.form.name}</span>
          <input name="name" required />
        </label>
        <label>
          <span>{t.form.message}</span>
          <textarea name="message" rows={5} defaultValue={t.contact.prompt} required />
        </label>
        <button className="pt-button pt-button-primary" type="submit">
          <MessageCircle size={19} /> {t.cta.whatsapp} <ArrowRight size={20} />
        </button>
        <p className="pt-contact-routing-note">
          {locale === "en"
            ? "WhatsApp opens with this message prepared."
            : "WhatsApp se abre con este mensaje preparado."}
        </p>
        {sent && <p className="form-success">{t.form.success}</p>}
      </form>
    </section>
  );
}

function SimplePage({ locale, page, navigate }: { locale: Locale; page: ContentPageKey; navigate: (href: string) => void }) {
  const t = copy[locale];
  const [title, body] =
    page === "app"
      ? locale === "en"
        ? ["Private client area", "Your sessions, habits and messages will be in one simple place when the portal is ready."]
        : ["Área privada del cliente", "Tus sesiones, hábitos y mensajes estarán en un lugar sencillo cuando el portal esté listo."]
      : t.legal[page];
  return (
    <section className="pt-page pt-simple-page">
      <div className="pt-page-hero">
        <span className="pt-eyebrow">{t.brand}</span>
        <h1>{title}</h1>
        <p>{body}</p>
        {page === "app" && (
          <div className="pt-app-preview">
            <PhonePreview locale={locale} />
          </div>
        )}
        <RouteLink page="contact" locale={locale} navigate={navigate} className="pt-button pt-button-primary">
          {t.cta.contact} <ArrowRight size={20} />
        </RouteLink>
      </div>
    </section>
  );
}

function PhonePreview({ locale }: { locale: Locale }) {
  return (
    <div className="pt-phone-preview">
      <span className="pt-phone-notch" />
      <strong>{locale === "en" ? "Today" : "Hoy"}</strong>
      <div className="pt-mini-ring" />
      <p>{locale === "en" ? "Mobility • Strength • Balance" : "Movilidad • Fuerza • Equilibrio"}</p>
    </div>
  );
}

function FinalCta({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const t = copy[locale];
  return (
    <section className="pt-final-cta">
      <div className="pt-brand-stamp" aria-hidden="true">
        <BrandGlyph />
      </div>
      <div>
        <h2>{locale === "en" ? "Ready to feel stronger in your day?" : "¿Quieres sentirte más fuerte en tu día a día?"}</h2>
        <p>{locale === "en" ? "Send a simple message. We will tell you where to start." : "Envía un mensaje sencillo. Te decimos por dónde empezar."}</p>
      </div>
      <RouteLink page="check" locale={locale} navigate={navigate} className="pt-button pt-button-primary">
        {t.cta.primary} <ArrowRight size={20} />
      </RouteLink>
    </section>
  );
}

function Footer({ locale, navigate }: { locale: Locale; navigate: (href: string) => void }) {
  const t = copy[locale];
  const isEn = locale === "en";
  const legalRail: Array<{ page: LegalPageKey; label: string }> = isEn
    ? [
        { page: "privacy", label: "Privacy Policy" },
        { page: "medicalDisclaimer", label: "Medical Disclaimer" },
        { page: "terms", label: "Terms of Use" },
        { page: "cookies", label: "Cookie Preferences" }
      ]
    : [
        { page: "privacy", label: "Política de Privacidad" },
        { page: "medicalDisclaimer", label: "Aviso médico" },
        { page: "terms", label: "Condiciones de Uso" },
        { page: "cookies", label: "Preferencias de Cookies" }
      ];

  return (
    <footer className="site-footer pt-footer">
      <div className="pt-footer-backdrop" aria-hidden="true">
        <StillImage src={images.routesCoast} alt="" />
      </div>
      <div className="pt-footer-main">
        <RouteLink page="home" locale={locale} navigate={navigate} className="brand-lockup pt-brand">
          <BrandMark />
          <span>
            <strong>{t.brand}</strong>
            <small>{t.byline}</small>
          </span>
        </RouteLink>
        <div className="pt-footer-hero">
          <h2>
            <WordReveal text={isEn ? "Improve your quality of life" : "Mejora tu calidad de vida"} />
          </h2>
          <RouteLink page="check" locale={locale} navigate={navigate} className="pt-footer-start">
            {isEn ? "Start here" : "Empezar"} <ArrowRight size={21} />
          </RouteLink>
        </div>
        <div className="pt-footer-columns">
          <nav aria-label={isEn ? "Learn" : "Aprende"}>
            <strong>{isEn ? "Learn" : "Aprende"}</strong>
            <RouteLink page="method" locale={locale} navigate={navigate}>
              {isEn ? "Method" : "Método"}
            </RouteLink>
            <RouteLink page="functional" locale={locale} navigate={navigate}>
              {isEn ? "Functional training" : "Entrenamiento funcional"}
            </RouteLink>
            <RouteLink page="plans" locale={locale} navigate={navigate}>
              {isEn ? "Plans" : "Planes"}
            </RouteLink>
            <RouteLink page="competition" locale={locale} navigate={navigate}>
              {isEn ? "Advanced" : "Avanzado"}
            </RouteLink>
          </nav>
          <nav aria-label="Jorge">
            <strong>Jorge</strong>
            <RouteLink page="jorge" locale={locale} navigate={navigate}>
              {isEn ? "About Jorge" : "Sobre Jorge"}
            </RouteLink>
            <RouteLink page="check" locale={locale} navigate={navigate}>
              {isEn ? "Start here" : "Empieza aquí"}
            </RouteLink>
            <RouteLink page="contact" locale={locale} navigate={navigate}>
              {isEn ? "Consultation" : "Consulta"}
            </RouteLink>
          </nav>
          <nav aria-label={isEn ? "Connect" : "Conecta"}>
            <strong>{isEn ? "Connect" : "Conecta"}</strong>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
              <Instagram size={16} /> Instagram
            </a>
            <a href={buildWhatsAppUrl(isEn ? "Hi, I would like to ask about personal training." : "Hola, me gustaría preguntar por el entrenamiento personal.")} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
              <MessageCircle size={16} /> WhatsApp
            </a>
          </nav>
        </div>
        <div className="pt-footer-bottom">
          <span>{isEn ? "© 2026 All Rights Reserved" : "© 2026 Todos los derechos reservados"}</span>
          <nav aria-label="Legal">
            {legalRail.map((item) => (
              <RouteLink key={item.page} page={item.page} locale={locale} navigate={navigate}>
                {item.label}
              </RouteLink>
            ))}
          </nav>
          <a className="credit" href="https://kemdrix-studio.io" target="_blank" rel="noreferrer">
            {t.footer.credit}
          </a>
        </div>
      </div>
    </footer>
  );
}

function ensureConsentMode() {
  if (typeof window === "undefined") return;
  const consentWindow = window as Window & {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __ptcsAnalyticsLoaded?: boolean;
  };
  consentWindow.dataLayer = consentWindow.dataLayer ?? [];
  consentWindow.gtag =
    consentWindow.gtag ??
    function gtag() {
      consentWindow.dataLayer?.push(arguments);
    };
  consentWindow.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500
  });

  // Load Google Analytics 4 once, only when a Measurement ID is configured.
  // Consent Mode v2 defaults above keep everything cookieless until the visitor
  // accepts, so the tag is safe to boot immediately (Advanced Consent Mode).
  if (
    GA_MEASUREMENT_ID &&
    GA_MEASUREMENT_ID.startsWith("G-") &&
    !consentWindow.__ptcsAnalyticsLoaded
  ) {
    consentWindow.__ptcsAnalyticsLoaded = true;
    consentWindow.gtag("js", new Date());
    consentWindow.gtag("config", GA_MEASUREMENT_ID);
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }
}

function updateConsentMode(choice: "accepted" | "declined") {
  if (typeof window === "undefined") return;
  const consentWindow = window as Window & { gtag?: (...args: unknown[]) => void };
  consentWindow.gtag?.("consent", "update", {
    analytics_storage: choice === "accepted" ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "granted",
    security_storage: "granted"
  });
}

function Consent({ locale }: { locale: Locale; page: PageKey }) {
  const [choice, setChoice] = useState<"accepted" | "declined" | null>(() => {
    try {
      const saved = localStorage.getItem(CONSENT_KEY);
      return saved === "accepted" || saved === "declined" ? saved : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    ensureConsentMode();
    if (choice) updateConsentMode(choice);
  }, [choice]);

  const saveChoice = (nextChoice: "accepted" | "declined") => {
    try {
      localStorage.setItem(CONSENT_KEY, nextChoice);
    } catch {
      // Consent remains valid for the current session even if storage is unavailable.
    }
    setChoice(nextChoice);
    updateConsentMode(nextChoice);
    if (nextChoice === "accepted") trackEvent("consent_update", { locale, choice: nextChoice });
  };

  if (choice) return null;

  return (
    <aside className="pt-consent-banner" role="dialog" aria-live="polite" aria-label={locale === "en" ? "Privacy preferences" : "Preferencias de privacidad"}>
      <div>
        <strong>{locale === "en" ? "Privacy" : "Privacidad"}</strong>
        <p>
          {locale === "en"
            ? "Essential cookies. Analytics only with permission."
            : "Cookies esenciales. Analítica solo con tu permiso."}
        </p>
        <a href={paths.cookies[locale]}>{locale === "en" ? "Cookies" : "Cookies"}</a>
      </div>
      <div className="pt-consent-actions">
        <button type="button" onClick={() => saveChoice("declined")}>
          {locale === "en" ? "Decline" : "Rechazar"}
        </button>
        <button type="button" onClick={() => saveChoice("accepted")}>
          {locale === "en" ? "Accept" : "Aceptar"}
        </button>
      </div>
    </aside>
  );
}

export default App;
